// Bigger UI panels: selection, diplomacy, help, defeat, chronicle — plus the
// small DOM/format helpers shared with hud.ts. Everything renders into #ui.

import { farmCard } from './farmCard'
import type { GameState } from '../state'
import type { ClientMsg } from '../../shared/protocol'
import type {
  Building,
  FactionPublic,
  GameEvent,
  Resource,
  Stockpile,
  UnitType,
  UnitWire,
} from '../../shared/types'
import type { BuildingType, ItemType } from '../../shared/types'
import { ARMORY_CAP, ARMORY_ITEMS, BARE_HAND_MULT, BUFFER_CAP, TOOL_GATHER_MULT, BUILDINGS, DAY_TICKS, TREE_GROW_TICKS, ITEMS, NAME_MAX, NPC_TIERS, UNITS, DEMOLISH_REFUND, UPKEEP_STORE_DAYS, buildingFootprint, farmYieldMult, upkeepDaily, isTerraform, siteBuildTime } from '../../shared/constants'
import { farmSkillMult, jobSkill, personTitle, requiredTool, skillMult } from '../../shared/person'
import { DIRS } from '../../shared/hex'
import { sitePrepPhase, terrainAt } from '../render/terrain'
import { fogOf, fogVisible } from '../render/fog'
import { TERRAIN, Terrain } from '../../shared/terrain'
import {
  ATTR_ABBR,
  ATTR_KEYS,
  SKILL_LABEL,
  ageYears,
  isArmed,
  personCombat,
  personMaxHp,
  unitMaxHp,
} from '../../shared/person'
import type { SkillKey } from '../../shared/types'
import { itemIconSvg } from './itemIcons'
import { CHANGELOG } from '../../shared/changelog'
import { BUILD_ID } from '../../shared/version'
import { realmMultiplier } from '../../shared/realm'
import { buildingHousing, buildingMaxHp, buildingStorage, nextTownHallUpgrade, TOWN_HALL_PHASES, townHallBonuses, townHallPhase } from '../../shared/townhall'
import type { TownHallPhase } from '../../shared/townhall'

// ---- Shared helpers -----------------------------------------------------------

export interface UICtx {
  st: GameState
  tx: (m: ClientMsg) => void
  root: HTMLElement
  jumpTo: (q: number, r: number) => void
}

export const RES_ICON: Record<Resource, string> = {
  food: '🌾',
  wood: '🪵',
  stone: '🪨',
  iron: '⛏️',
  gold: '🪙',
  dirt: '🟫',
  sand: '🟨',
}

export const BUILD_ICON: Record<BuildingType, string> = {
  townhall: '🏕️',
  house: '🏠',
  workbench: '🛠️',
  dummy: '🎯',
  palisade: '🪵',
  pier: '🎣',
  farm: '🌾',
  chop: '🪓',
  plant: '🌱',
  quarry: '🧱',
  mine: '⛏️',
  storehouse: '📦',
  barracks: '⚔️',
  tower: '🗼',
  ramp: '⛰️',
  dig: '🪏',
  fill: '⏫',
  fillsand: '🏖️',
  road: '🛤️',
  camp: '🏴',
}

export const UNIT_ICON: Record<UnitType, string> = {
  citizen: '🧑',
  worker: '👷',
  carrier: '🧺',
  spearman: '🗡️',
  archer: '🏹',
  raider: '🏴‍☠️',
  wolf: '🐺',
  bear: '🐻',
  deer: '🦌',
}

/** What a working person is doing right now, by the building they're staffing. */
const JOB_VERB: Partial<Record<BuildingType, string>> = {
  farm: 'farming', quarry: 'quarrying', mine: 'mining',
}

/** Game-hours a starving serf has gone without food, or null when fed. */
export function starvingHours(st: GameState, p: { food?: number; starvedAt?: number }): number | null {
  if ((p.food ?? 100) > 0 || p.starvedAt === undefined) return null
  return Math.max(0, (st.tick - p.starvedAt) / (DAY_TICKS / 24))
}

/** True when the satchel actually holds something (u.carrying may be an empty {}). */
function hasGoods(u: UnitWire): boolean {
  return !!u.carrying && Object.values(u.carrying).some((v) => (v ?? 0) > 0)
}

/** The building the unit's route ends at (its wpath tail, else its logical hex). */
function destBuilding(st: GameState, u: UnitWire): Building | undefined {
  const end = u.wpath && u.wpath.length > 0 ? u.wpath[u.wpath.length - 1] : { q: u.q, r: u.r }
  for (const b of st.buildings.values()) {
    for (const f of buildingFootprint(b.type, b.q, b.r)) {
      if (f.q === end.q && f.r === end.r) return b
    }
  }
  return undefined
}

/** "depositing in "The Old Mill"" when the goods' destination is known. */
function depositText(st: GameState, u: UnitWire): string | null {
  const b = destBuilding(st, u)
  return b ? `depositing in "${b.name || BUILDINGS[b.type].name}"` : null
}

/** What a unit is doing right now, in plain words (shared by panel + hover tip). */
export function unitActivity(st: GameState, u: UnitWire): string {
  if (u.raidLoot && hasGoods(u)) {
    const loot = u.raidLoot === st.me ? 'your stolen supplies' : 'stolen supplies'
    if (u.state === 'fighting') return `fighting while carrying ${loot}`
    if (u.state === 'fleeing') return `fleeing with ${loot}`
    return `carrying ${loot} home`
  }
  if (u.exploring) {
    if (u.state === 'feeding') return 'taking a food break from exploring'
    if (u.state === 'fighting') return 'fighting while exploring'
    if (u.state === 'fleeing') return 'fleeing danger while exploring'
    return u.state === 'idle' ? 'choosing unexplored ground' : 'exploring the map'
  }
  if (u.supply !== undefined) {
    return u.carrying ? 'delivering building materials' : 'fetching building materials'
  }
  if (u.scav) return 'scavenging for wood & stone'
  if (u.patrol && (u.state === 'moving' || u.state === 'idle')) return 'on patrol'
  if (u.hauler) {
    return u.state === 'hauling'
      ? (hasGoods(u) ? depositText(st, u) ?? 'hauling goods home' : 'off to collect goods')
      : 'waiting for a haul'
  }
  switch (u.state) {
    case 'waitingStorage': return 'waiting for storage space'
    case 'feeding': return 'heading home to eat'
    case 'errand': return u.errand?.swap != null ? 'swapping gear at the armory' : 'heading to the armory'
    case 'building': {
      const b = u.job !== undefined ? st.buildings.get(u.job) : undefined
      if (b?.type === 'chop') return 'chopping trees'
      if (b?.type === 'plant') return 'planting a tree'
      if (b && isTerraform(b.type)) return 'shifting earth'
      if (b?.done) return 'repairing ' + (b.name || BUILDINGS[b.type].name).toLowerCase()
      return b ? 'building a ' + BUILDINGS[b.type].name.toLowerCase() : 'building a site'
    }
    case 'working': {
      const b = u.job !== undefined ? st.buildings.get(u.job) : undefined
      return (b && JOB_VERB[b.type]) || 'working'
    }
    case 'toJob': return 'heading to work'
    case 'hauling': return hasGoods(u) ? depositText(st, u) ?? 'hauling goods' : 'hauling goods'
    case 'fighting': return 'fighting!'
    case 'fleeing': return 'fleeing'
    case 'moving': return 'on the move'
    case 'roaming': return 'roaming'
    default: return 'idle'
  }
}

/** Like el(), but the content is trusted HTML (item SVG icons + static labels). */
function elHtml<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls: string,
  html: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  e.className = cls
  e.innerHTML = html
  return e
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  if (cls) e.className = cls
  if (text !== undefined) e.textContent = text
  return e
}

export function fmtCost(cost: Partial<Stockpile> | undefined): string {
  if (!cost) return 'free'
  const parts: string[] = []
  for (const [k, v] of Object.entries(cost)) {
    if (v) parts.push(RES_ICON[k as Resource] + String(Math.ceil(v)))
  }
  return parts.length ? parts.join(' ') : 'free'
}

// ---- Craft hover card ----------------------------------------------------------
// Hovering a forge button shows what the item is FOR, not just its price. One
// document-level delegate (buttons are rebuilt every panel refresh, so per-node
// listeners would go stale mid-hover); the card reuses the resource-tip skin.
let craftTip: HTMLElement | null = null

function showCraftTip(anchor: HTMLElement, it: ItemType, status: string | null): void {
  if (!craftTip) return
  const idef = ITEMS[it]
  craftTip.innerHTML = ''
  const head = el('div', 'rt-head')
  head.appendChild(elHtml('span', 'rt-ico', itemIconSvg(it, 16)))
  head.appendChild(el('span', 'rt-name', idef.name))
  craftTip.appendChild(head)
  const stats = idef.kind === 'weapon' ? ` · ⚔️ ${idef.atk} · ${(idef.range ?? 0) > 0 ? `${idef.range}-hex reach` : 'melee'}` : ''
  craftTip.appendChild(el('div', 'rt-sub', `${fmtCost(idef.forgeCost)} · ⏳ ${idef.forgeTime}s${stats}`))
  craftTip.appendChild(el('div', 'rt-desc', idef.desc))
  craftTip.appendChild(el('div', 'rt-empty', status ?? 'Paid bit by bit while it’s worked — the finished piece lands in the armory.'))
  craftTip.classList.remove('hidden')
  // The panel hugs a screen edge: lay the card toward the map, clamped on-screen.
  const r = anchor.getBoundingClientRect()
  let left = r.left - craftTip.offsetWidth - 10
  if (left < 8) left = Math.min(window.innerWidth - craftTip.offsetWidth - 8, r.right + 10)
  const top = Math.max(8, Math.min(r.top, window.innerHeight - craftTip.offsetHeight - 8))
  craftTip.style.left = Math.round(left) + 'px'
  craftTip.style.top = Math.round(top) + 'px'
}

function wireCraftTip(): void {
  if (craftTip) return
  craftTip = el('div', 'restip crafttip hidden')
  // Same host as the resource tooltip — #ui owns the HUD stacking context.
  ;(document.getElementById('ui') ?? document.body).appendChild(craftTip)
  document.addEventListener('mouseover', (e) => {
    const t = (e.target as HTMLElement | null)?.closest?.('[data-craft-item]') as HTMLElement | null
    if (t?.dataset.craftItem) showCraftTip(t, t.dataset.craftItem as ItemType, t.dataset.craftStatus || null)
    else craftTip?.classList.add('hidden')
  })
}

// ---- Workplace-chip hover ------------------------------------------------------
// Hovering a serf's workplace chip silhouettes that building in the world
// (st.uiHover — both renderers honor it). One document-level delegate: the
// panel rebuilds its DOM every tick, so per-node mouseleave never fires
// reliably; any mouseover that ISN'T on a chip clears the highlight.
let jobHoverSt: GameState | null = null
let jobHoverWired = false
function wireJobHoverDelegate(st: GameState): void {
  jobHoverSt = st
  if (jobHoverWired) return
  jobHoverWired = true
  document.addEventListener('mouseover', (e) => {
    if (!jobHoverSt) return
    const t = (e.target as HTMLElement | null)?.closest?.('[data-job-b]') as HTMLElement | null
    if (t?.dataset.jobB) jobHoverSt.uiHover = { kind: 'building', id: Number(t.dataset.jobB) }
    else if (jobHoverSt.uiHover) jobHoverSt.uiHover = null
  })
}

export function canAfford(st: GameState, cost: Partial<Stockpile> | undefined): boolean {
  if (!cost) return true
  const self = st.self
  if (!self) return false
  for (const [k, v] of Object.entries(cost)) {
    if ((v ?? 0) > (self.stockpile[k as Resource] ?? 0)) return false
  }
  return true
}

/** A little progress/hp bar. frac 0..1; tone auto-colors by fraction unless cls given. */
export function bar(frac: number, cls?: string): HTMLElement {
  const f = Math.max(0, Math.min(1, frac))
  const wrap = el('div', 'bar' + (cls ? ' ' + cls : ''))
  const fill = el('div', 'bar-fill')
  if (!cls) {
    fill.classList.add(f > 0.6 ? 'ok' : f > 0.3 ? 'warn' : 'bad')
  }
  fill.style.width = (f * 100).toFixed(1) + '%'
  wrap.appendChild(fill)
  return wrap
}

export function nameOf(st: GameState, id: string): string {
  return st.factions.get(id)?.name ?? id
}

export function colorOf(st: GameState, id: string): string {
  return st.factions.get(id)?.color ?? '#b7a988'
}

function terrainNames(list: Terrain[] | undefined): string {
  if (!list || !list.length) return ''
  return list.map((t) => TERRAIN[t].name).join(', ')
}

/** Tooltip body for a build-bar button. */
export function buildTooltip(type: BuildingType): HTMLElement {
  const def = BUILDINGS[type]
  const tip = el('div', 'tip')
  const head = el('div', 'tip-head')
  head.appendChild(el('span', 'tip-icon', BUILD_ICON[type]))
  head.appendChild(el('span', 'tip-name', def.name))
  tip.appendChild(head)
  tip.appendChild(el('div', 'tip-desc', def.desc))
  const meta = el('div', 'tip-meta')
  meta.appendChild(el('span', 'tip-cost', fmtCost(def.cost)))
  meta.appendChild(el('span', 'tip-time', type === 'plant' ? '🌱 3 game days to grow' : '⏳ ~' + def.buildTime + 's'))
  tip.appendChild(meta)
  const reqs: string[] = []
  if (type === 'plant') reqs.push('No tools needed · villagers plant, then return to work')
  if (type === 'pier') reqs.push('⟳ R to rotate while placing')
  if (def.on) reqs.push('On: ' + terrainNames(def.on))
  if (def.adjacent) reqs.push('Beside: ' + terrainNames(def.adjacent))
  if (type === 'townhall') reqs.push('👥 Housing grows with upgrades')
  else if (buildingHousing({ type })) reqs.push('👥 +' + buildingHousing({ type }) + ' housing')
  if (buildingStorage({ type })) reqs.push('📦 +' + buildingStorage({ type }) + ' storage')
  if (def.attack) reqs.push('🎯 dmg ' + def.attack.dmg + ' · range ' + def.attack.range)
  for (const r of reqs) tip.appendChild(el('div', 'tip-req', r))
  return tip
}

// ---- Selection panel ------------------------------------------------------------

export function initSelectionPanel(ctx: UICtx): { refresh: () => void } {
  const { st, tx, root, jumpTo } = ctx
  const panel = el('div', 'panel sel-panel hidden')
  root.appendChild(panel)

  let demolishArmedFor = -1
  let demolishArmedAt = 0
  let renderedBuildingId: number | null = null

  // Only the request is local: phase, materials and timer always come from the
  // server. Keep this guard across the panel's per-tick DOM replacements.
  const pendingHallUpgrades = new Map<number, { phase: TownHallPhase; at: number }>()
  const clearHallRequests = (): void => {
    if (!pendingHallUpgrades.size) return
    pendingHallUpgrades.clear()
    refresh()
  }
  st.on('error', clearHallRequests)
  st.on('welcome', clearHallRequests)
  st.on('connection', (connected) => { if (!connected) clearHallRequests() })

  // In-progress rename, if any. While editing the still-selected entity, refresh()
  // skips its per-tick re-render so the live <input> isn't wiped from under the user.
  let editing: { kind: 'b' | 'u'; id: number } | null = null

  function hide(): void {
    renderedBuildingId = null
    panel.classList.add('hidden')
    panel.replaceChildren()
  }

  function hallUpgradeMissing(b: Building): string[] {
    const next = nextTownHallUpgrade(b.hallPhase)
    if (!next || !st.self) return []
    const missing: string[] = []
    const people = Math.max(0, next.population - st.self.pop)
    if (people) missing.push(`${people} more ${people === 1 ? 'person' : 'people'}`)
    for (const [key, amount] of Object.entries(next.cost)) {
      const short = Math.ceil((amount ?? 0) - (st.self.stockpile[key as Resource] ?? 0))
      if (short > 0) missing.push(`${short} ${key}`)
    }
    return missing
  }

  /** Public architecture, then an owner-only upgrade plan. */
  function hallUpgradeCard(b: Building): HTMLElement {
    const phase = townHallPhase(b.hallPhase)
    const card = el('div', 'hall-upgrade-card')
    const heading = el('div', 'hall-phase-heading')
    heading.appendChild(el('strong', '', TOWN_HALL_PHASES[phase - 1].name))
    heading.appendChild(el('span', '', `Phase ${phase} of 5`))
    card.appendChild(heading)
    const steps = el('ol', 'hall-phase-steps')
    steps.setAttribute('aria-label', 'Town Hall phases')
    const shortNames = ['Camp', 'Settlers', 'Timber', 'Fortified', 'Keep']
    for (const stage of TOWN_HALL_PHASES) {
      const step = el('li', stage.phase <= phase ? 'reached' : '')
      step.title = `${stage.phase}. ${stage.name}${stage.phase === phase ? ' (current)' : ''}`
      if (stage.phase === phase) step.setAttribute('aria-current', 'step')
      if (stage.phase === b.hallUpgrade?.phase) step.classList.add('upgrading')
      step.appendChild(el('span', 'hall-phase-number', String(stage.phase)))
      step.appendChild(el('span', 'hall-phase-name', shortNames[stage.phase - 1]))
      steps.appendChild(step)
    }
    card.appendChild(steps)
    // Foreign halls expose their architecture, never our inventory or theirs.
    if (b.owner !== st.me || st.observer) return card

    const pending = pendingHallUpgrades.get(b.id)
    if (pending && (b.hallUpgrade || pending.phase !== phase || Date.now() - pending.at >= 4000)) {
      pendingHallUpgrades.delete(b.id)
    }
    const next = nextTownHallUpgrade(phase)
    if (!next) {
      card.appendChild(el('div', 'hall-upgrade-complete', '✓ Fully upgraded'))
      return card
    }
    const target = b.hallUpgrade?.phase ?? next.phase
    card.appendChild(el('div', 'hall-next-name', `${b.hallUpgrade ? 'Upgrading to' : 'Next:'} ${TOWN_HALL_PHASES[target - 1].name}`))
    const currentBonus = townHallBonuses(phase)
    const nextBonus = townHallBonuses(target)
    const benefits = el('div', 'hall-upgrade-benefits')
    benefits.appendChild(el('span', '', `+${nextBonus.housing - currentBonus.housing} housing`))
    benefits.appendChild(el('span', '', `+${nextBonus.storage - currentBonus.storage} storage`))
    benefits.appendChild(el('span', '', `+${nextBonus.hp - currentBonus.hp} max health`))
    card.appendChild(benefits)
    if (phase === 1) {
      card.appendChild(el('div', 'hall-upgrade-note', 'Unlocks Village Watch: two spare villagers craft and carry spears to guard your camp.'))
      const project = el('button', 'btn btn-ghost', st.self?.camp?.active ? 'View camp project in Council' : 'Plan this upgrade in Council')
      project.type = 'button'
      project.onclick = () => st.emit('openCouncil')
      card.appendChild(project)
    }

    if (b.hallUpgrade) {
      const { started, ends } = b.hallUpgrade
      const fraction = Math.max(0, Math.min(1, (st.tick - started) / Math.max(1, ends - started)))
      const remaining = Math.max(0, Math.ceil(ends - st.tick))
      const timer = el('div', 'hall-upgrade-timer')
      timer.appendChild(el('strong', '', b.paused ? '⏸ Upgrade paused' : remaining ? `${remaining}s remaining` : 'Finishing upgrade…'))
      timer.appendChild(el('span', '', `${Math.floor(fraction * 100)}%`))
      card.appendChild(timer)
      const progress = el('progress', 'hall-upgrade-progress')
      progress.max = 1
      progress.value = fraction
      progress.setAttribute('aria-label', `Upgrade to ${TOWN_HALL_PHASES[target - 1].name}`)
      card.appendChild(progress)
      card.appendChild(el('div', 'hall-upgrade-note', b.paused
        ? 'Use Resume work below to continue. Materials are already paid.'
        : 'Materials paid. Improvements apply when the upgrade is complete.'))
      return card
    }

    const pop = st.self?.pop ?? 0
    const population = el('div', 'hall-upgrade-pop' + (pop < next.population ? ' missing' : ''))
    population.appendChild(el('span', '', 'Village population'))
    population.appendChild(el('strong', '', `${pop} / ${next.population}`))
    card.appendChild(population)
    const costs = el('div', 'hall-upgrade-costs')
    costs.setAttribute('aria-label', 'Materials available and required')
    for (const [key, amount] of Object.entries(next.cost)) {
      const res = key as Resource
      const have = Math.floor(st.self?.stockpile[res] ?? 0)
      const need = Math.ceil(amount ?? 0)
      const row = el('div', 'hall-upgrade-cost' + (have < need ? ' missing' : ''))
      row.appendChild(el('span', '', `${RES_ICON[res]} ${res}`))
      row.appendChild(el('strong', '', `${have} / ${need}`))
      costs.appendChild(row)
    }
    card.appendChild(costs)
    const missing = hallUpgradeMissing(b)
    const waiting = pendingHallUpgrades.has(b.id)
    const unavailable = !st.connected || !st.self || st.self.ruined
    const button = el('button', 'btn btn-gold hall-upgrade-button', waiting ? 'Starting upgrade…' : `Upgrade · ${next.duration}s`)
    button.type = 'button'
    button.dataset.upgradeHall = String(b.id)
    button.disabled = waiting || unavailable || missing.length > 0
    const reason = !st.connected ? 'Reconnect to upgrade.'
      : !st.self || st.self.ruined ? 'Your village must be active to upgrade.'
      : waiting ? 'Waiting for your village to confirm the upgrade.'
      : missing.length ? `Need ${missing.join(', ')}.`
      : b.paused ? 'Materials are paid now. Resume work to start the timer.'
      : 'Materials are paid now. Your villagers keep working during the upgrade.'
    button.title = reason
    const request = (): void => {
      const live = st.buildings.get(b.id)
      if (pendingHallUpgrades.has(b.id) || !live || live.owner !== st.me || !live.done || live.hallUpgrade ||
          townHallPhase(live.hallPhase) !== phase || !st.connected || !st.self || st.self.ruined || hallUpgradeMissing(live).length) return
      const request = { phase, at: Date.now() }
      pendingHallUpgrades.set(b.id, request)
      button.disabled = true
      button.textContent = 'Starting upgrade…'
      tx({ t: 'upgradeHall', id: b.id, phase })
      // Lost acknowledgements must never leave a permanently disabled control.
      setTimeout(() => {
        if (pendingHallUpgrades.get(b.id) !== request) return
        pendingHallUpgrades.delete(b.id)
        if (st.selection.building === b.id) refresh()
      }, 4000)
    }
    // A pointer press can straddle the next tick's DOM replacement. Act on the
    // press; the pending guard also suppresses its follow-up click. Keyboard and
    // assistive-technology activation still work through the click listener.
    button.onpointerdown = (e) => { if (e.button === 0 && !button.disabled) { e.preventDefault(); request() } }
    button.onclick = request
    card.appendChild(button)
    card.appendChild(el('div', 'hall-upgrade-note' + (missing.length ? ' missing' : ''), reason))
    return card
  }

  /** A panel title that a player can rename in place (pencil → inline input). */
  function nameTitle(kind: 'b' | 'u', id: number, current: string, fallback: string, canEdit: boolean): HTMLElement {
    if (editing && editing.kind === kind && editing.id === id) {
      const input = el('input', 'name-input') as HTMLInputElement
      input.type = 'text'
      input.value = current
      input.maxLength = NAME_MAX
      input.placeholder = fallback
      let closed = false
      const finish = (save: boolean): void => {
        if (closed) return
        closed = true
        const name = input.value.replace(/\s+/g, ' ').trim().slice(0, NAME_MAX)
        editing = null
        if (save) tx({ t: 'rename', kind, id, name })
        refresh()
      }
      input.addEventListener('keydown', (e) => {
        e.stopPropagation() // don't fire game hotkeys while typing
        if (e.key === 'Enter') finish(true)
        else if (e.key === 'Escape') finish(false)
      })
      input.addEventListener('blur', () => finish(true))
      setTimeout(() => { input.focus(); input.select() }, 0)
      return input
    }
    const wrap = el('div', 'sel-name-row')
    wrap.appendChild(el('div', 'sel-name', current || fallback))
    if (canEdit) {
      const pencil = el('button', 'name-edit-btn', '✎') as HTMLButtonElement
      pencil.title = 'Rename'
      pencil.onclick = () => { editing = { kind, id }; refresh(true) }
      wrap.appendChild(pencil)
    }
    return wrap
  }

  type BuildingTab = 'info' | 'craft' | 'storage' | 'crew' | 'armory'
  const buildingTabs = new Map<number, BuildingTab>()
  const armoryItems = [...ARMORY_ITEMS]
    .sort((a, b) => Number(ITEMS[a].kind === 'weapon') - Number(ITEMS[b].kind === 'weapon'))
  const pendingArmory = new Map<ItemType, { min: number; at: number }>()
  const clearArmoryRequests = (): void => {
    pendingArmory.clear()
    if (panel.dataset.activeBuildingTab === 'armory') refresh(true)
  }
  st.on('error', clearArmoryRequests)
  st.on('welcome', clearArmoryRequests)
  st.on('connection', clearArmoryRequests)

  function armoryMinimums(): Partial<Record<ItemType, number>> {
    const minimums = { ...st.self?.armoryMinimums }
    for (const [item, pending] of pendingArmory) {
      if ((minimums[item] ?? 0) === pending.min || Date.now() - pending.at >= 4000) pendingArmory.delete(item)
      else minimums[item] = pending.min
    }
    return minimums
  }

  function canSetArmory(b: Building): boolean {
    const live = st.buildings.get(b.id)
    return !!(st.connected && st.self && !st.self.ruined && !st.observer && live?.owner === st.me && live.done)
  }

  function setArmoryMinimum(b: Building, item: ItemType, min: number): boolean {
    if (!canSetArmory(b) || !Number.isInteger(min) || min < 0) return false
    const minimums = armoryMinimums()
    const others = armoryItems.reduce((sum, it) => sum + (it === item ? 0 : minimums[it] ?? 0), 0)
    if (others + min > ARMORY_CAP) return false
    if ((minimums[item] ?? 0) === min) return true
    const request = { min, at: Date.now() }
    pendingArmory.set(item, request)
    tx({ t: 'armoryMinimum', item, min })
    refresh(true)
    setTimeout(() => {
      if (pendingArmory.get(item) !== request) return
      pendingArmory.delete(item)
      if (panel.dataset.activeBuildingTab === 'armory') refresh()
    }, 4000)
    return true
  }

  // Keep a touch gesture outside the replaceable button DOM. A live refresh can
  // replace its target between touch-down and release; a swipe must never craft.
  const pressActions = new WeakMap<HTMLButtonElement, () => void>()
  const pressKey = (button: HTMLButtonElement): string => {
    for (const name of ['craftItem', 'buildingAction', 'armoryControl'] as const) {
      if (button.dataset[name]) return `${name}:${button.dataset[name]}`
    }
    return button.textContent ?? ''
  }
  const selectionPressKey = (): string => `${st.me}:${st.selection.building}:${st.selection.units.join(',')}:${panel.dataset.activeBuildingTab ?? ''}`
  let touchPress: { id: number; x: number; y: number; key: string; selection: string } | null = null
  const clearTouchPress = (): void => { touchPress = null }
  window.addEventListener('pointerdown', e => {
    if (e.pointerType === 'touch' && !e.isPrimary) clearTouchPress()
  }, true)
  window.addEventListener('pointermove', e => {
    if (touchPress?.id === e.pointerId && Math.hypot(e.clientX - touchPress.x, e.clientY - touchPress.y) > 8) clearTouchPress()
  }, { capture: true, passive: true })
  window.addEventListener('pointercancel', clearTouchPress, true)
  window.addEventListener('blur', clearTouchPress)
  window.addEventListener('pointerup', e => {
    const press = touchPress
    if (!press || press.id !== e.pointerId) return
    clearTouchPress()
    if (Math.hypot(e.clientX - press.x, e.clientY - press.y) > 8 ||
      press.selection !== selectionPressKey() || panel.classList.contains('hidden')) return
    // Resolve the current button and closure so disabled states and live queue
    // limits remain authoritative even when the original node no longer exists.
    if (document.activeElement instanceof HTMLInputElement && panel.contains(document.activeElement)) document.activeElement.blur()
    const button = [...panel.querySelectorAll<HTMLButtonElement>('button')]
      .find(candidate => pressActions.has(candidate) && pressKey(candidate) === press.key)
    if (button && !button.disabled) pressActions.get(button)?.()
  }, true)

  // Pointer clicks are handled once above (touch) or at pointer-down (mouse).
  // A synthetic click may land on a replacement button, so a per-node guard is
  // insufficient. Zero-detail clicks retain keyboard and accessibility support.
  function pressAction(button: HTMLButtonElement, action: () => void): void {
    pressActions.set(button, action)
    button.onpointerdown = (e) => {
      if (e.button !== 0 || button.disabled) return
      if (e.pointerType === 'touch') {
        if (e.isPrimary) touchPress = { id: e.pointerId, x: e.clientX, y: e.clientY, key: pressKey(button), selection: selectionPressKey() }
        return
      }
      e.preventDefault()
      if (document.activeElement instanceof HTMLInputElement && panel.contains(document.activeElement)) document.activeElement.blur()
      action()
    }
    button.onclick = e => {
      if (e.detail === 0 && !button.disabled) action()
    }
  }

  function tabBar(b: Building, tabs: { key: BuildingTab; label: string }[], active: BuildingTab): HTMLElement {
    const row = el('div', 'sel-tabs')
    row.setAttribute('role', 'tablist')
    row.setAttribute('aria-label', 'Building details')
    row.style.setProperty('--tab-columns', String(tabs.length <= 3 ? tabs.length : Math.ceil(tabs.length / 2)))
    const activate = (key: BuildingTab): void => {
      if (buildingTabs.get(b.id) === key) return
      // Commit any edited storage limit before switching its content away.
      if (document.activeElement instanceof HTMLInputElement && panel.contains(document.activeElement)) document.activeElement.blur()
      buildingTabs.set(b.id, key)
      craftTip?.classList.add('hidden')
      refresh(true)
      panel.querySelector<HTMLButtonElement>(`[data-building-tab="${key}"]`)?.focus({ preventScroll: true })
    }
    tabs.forEach(({ key, label }, index) => {
      const button = el('button', 'sel-tab', label)
      button.type = 'button'
      button.id = `building-${b.id}-tab-${key}`
      button.dataset.buildingTab = key
      button.setAttribute('role', 'tab')
      button.setAttribute('aria-selected', String(key === active))
      button.setAttribute('aria-controls', `building-${b.id}-pane-${key}`)
      button.tabIndex = key === active ? 0 : -1
      button.onpointerdown = (e) => { if (e.button === 0) { e.preventDefault(); activate(key) } }
      button.onclick = () => activate(key)
      button.onkeydown = (e) => {
        let next: number
        if (e.key === 'ArrowRight') next = (index + 1) % tabs.length
        else if (e.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length
        else if (e.key === 'Home') next = 0
        else if (e.key === 'End') next = tabs.length - 1
        else return
        e.preventDefault(); e.stopPropagation()
        activate(tabs[next].key)
      }
      row.appendChild(button)
    })
    return row
  }

  /** A section within the selected tab; its controls are immediately visible. */
  function section(key: string, icon: string, title: string, summary: string, host: HTMLElement): { body: HTMLElement; sum: HTMLElement } {
    const root = el('div', 'sel-sec')
    root.dataset.section = key
    const head = el('div', 'sel-section-heading')
    head.appendChild(el('span', 'sec-icon', icon))
    head.appendChild(el('span', 'sec-title', title))
    const sum = el('span', 'sec-sum', summary)
    head.appendChild(sum)
    const body = el('div', 'sel-sec-body')
    root.append(head, body)
    host.appendChild(root)
    return { body, sum }
  }

  function renderBuilding(b: Building): void {
    const def = BUILDINGS[b.type]
    const slots = def.workerSlots ?? 0
    panel.replaceChildren()
    panel.classList.remove('hidden')
    const tabbed = b.type !== 'plant' && b.done && b.owner === st.me && !st.observer
    panel.classList.toggle('building-panel', tabbed)
    const tabs: { key: BuildingTab; label: string }[] = [{ key: 'info', label: 'Info' }]
    if (def.forges?.length || def.trains?.length) tabs.push({ key: 'craft', label: 'Crafting' })
    if (b.type === 'townhall' || b.type === 'storehouse') tabs.push({ key: 'storage', label: 'Storage' })
    if (slots > 0) tabs.push({ key: 'crew', label: 'Workers' })
    if (b.type === 'townhall') tabs.push({ key: 'armory', label: 'Armory' })
    const preferred = buildingTabs.get(b.id) ?? 'info'
    const active: BuildingTab = tabbed && tabs.some(t => t.key === preferred) ? preferred : 'info'
    panel.dataset.activeBuildingTab = active
    const content = tabbed ? el('div', 'sel-tab-content') : panel

    // Header: the icon sits in a crest, with the name and a type · owner line.
    const head = el('div', 'sel-head')
    const crest = el('div', 'sel-crest')
    crest.appendChild(el('span', 'sel-icon', BUILD_ICON[b.type]))
    head.appendChild(crest)
    const title = el('div', 'sel-title')
    const label = b.type === 'plant' ? b.done ? 'Growing tree' : 'Plant a tree' : b.done ? b.name || def.name : def.name + ' (site)'
    title.appendChild(nameTitle('b', b.id, label, def.name, b.type !== 'plant' && b.owner === st.me && b.done))
    const owner = b.owner === st.me ? 'Yours' : nameOf(st, b.owner) + (st.factions.get(b.owner)?.controller === 'neutral' ? ' · Neutral village · AI' : '')
    title.appendChild(
      el('div', 'sel-sub', b.type === 'plant' ? `Forestry · ${owner}` : b.done && b.name ? `${def.name} · ${owner}` : b.done ? owner : `Building site · ${owner}`),
    )
    head.appendChild(title)
    panel.appendChild(head)

    if (b.type === 'plant') {
      const remaining = Math.max(0, (b.treeGrowsAt ?? st.tick + TREE_GROW_TICKS) - st.tick)
      const fraction = b.done ? Math.max(0, Math.min(1, 1 - remaining / TREE_GROW_TICKS)) : 0
      const card = el('div', 'forestry-card')
      const stages = el('div', 'forestry-stages')
      for (const [i, [icon, name]] of [['🌱', 'Seedling'], ['🌿', 'Sapling'], ['🌳', 'Young tree']].entries()) {
        const stage = el('div', 'forestry-stage' + (b.done && fraction >= i / 3 ? ' reached' : ''))
        const picture = el('span', '', icon); picture.setAttribute('aria-hidden', 'true')
        stage.append(picture, el('span', '', name)); stages.append(stage)
      }
      card.append(stages)
      const hours = Math.ceil(remaining * 24 / DAY_TICKS)
      const days = Math.floor(hours / 24), tail = hours % 24
      const left = [days ? `${days} game day${days === 1 ? '' : 's'}` : '', tail ? `${tail}h` : ''].filter(Boolean).join(' · ')
      const workNeeded = Math.min(b.workers.length ? 1 : 3, Math.max(0, def.buildTime - b.progress))
      const foodNeeded = (def.cost.food ?? 0) * workNeeded / def.buildTime
      const waitingForFood = !b.done && (b.buffer.food ?? 0) + 1e-9 < foodNeeded
      const status = b.done ? remaining > 0 ? `${left} left to grow` : 'Finishing growth…'
        : waitingForFood ? 'Waiting for food delivery'
        : b.progress > 0 ? 'Your villager is planting…' : 'Waiting for a free villager to plant'
      card.append(el('strong', 'forestry-status', status))
      const meter = el('progress', 'forestry-meter')
      meter.max = b.done ? TREE_GROW_TICKS : def.buildTime
      meter.value = b.done ? fraction * TREE_GROW_TICKS : b.progress
      meter.setAttribute('aria-label', b.done ? 'Tree growth' : 'Planting progress')
      card.append(meter, el('p', 'sel-hint', b.done
        ? 'Growing on its own. No workers or supplies needed. Once mature, use Chop Trees to harvest its wood.'
        : 'Costs 1 food per hex, delivered by your villagers. No tools needed. After planting, it grows on its own for 3 game days.'))
      panel.append(card)
      if (b.owner === st.me && !st.observer) {
        const armed = demolishArmedFor === b.id && Date.now() - demolishArmedAt < 5000
        const remove = el('button', 'btn btn-danger forestry-remove', armed ? 'Confirm removal?' : b.done ? 'Uproot sapling' : 'Cancel planting')
        remove.dataset.buildingAction = 'remove-plant'
        remove.disabled = !st.connected
        pressAction(remove, () => {
          if (armed) { tx({ t: 'demolish', id: b.id }); demolishArmedFor = -1 }
          else { demolishArmedFor = b.id; demolishArmedAt = Date.now() }
          refresh()
        })
        panel.append(remove)
      }
      return
    }

    if (tabbed) {
      panel.appendChild(tabBar(b, tabs, active))
      content.id = `building-${b.id}-pane-${active}`
      content.setAttribute('role', 'tabpanel')
      content.setAttribute('aria-labelledby', `building-${b.id}-tab-${active}`)
      content.tabIndex = 0
      panel.appendChild(content)
      // Keep tab relationships valid without rendering inactive controls.
      for (const tab of tabs) if (tab.key !== active) {
        const empty = el('div', 'hidden')
        empty.id = `building-${b.id}-pane-${tab.key}`
        empty.setAttribute('role', 'tabpanel')
        empty.setAttribute('aria-labelledby', `building-${b.id}-tab-${tab.key}`)
        panel.appendChild(empty)
      }
    }

    // Vitals and improvements belong to Info, leaving recipes at the top.
    if (active === 'info') {
      if (b.type === 'townhall' && b.done) {
        content.appendChild(hallUpgradeCard(b))
      }
      const vit = el('div', 'sel-vitals')
      const hp = el('div', 'vital')
      hp.appendChild(el('span', 'vital-ico', '❤'))
      hp.appendChild(bar(b.hp / buildingMaxHp(b)))
      hp.appendChild(el('span', 'vital-val', `${Math.ceil(b.hp)}/${buildingMaxHp(b)}`))
      vit.appendChild(hp)
      content.appendChild(vit)

      // What this building is making RIGHT NOW — the headline stat for a producer,
      // no longer buried as an italic aside in the Workers section.
      if (b.done && def.produces && slots > 0) {
        const yc = productionCard(b, slots)
        if (yc) content.appendChild(yc)
      }
    }

    // Construction site (or a chop / terraform order in progress)
    if (!b.done) {
      // Site preparation: the crew clears and levels the plot before the blueprint —
      // name the phase so the see-through overlay on the map explains itself. The
      // ✕ Demolish button below cancels the works at any point.
      const prepPhase = sitePrepPhase(st, b)
      if (prepPhase) {
        panel.appendChild(
          el(
            'div',
            'sel-hint',
            prepPhase === 'clear'
              ? '🪓 Preparing the site: the crew fells the trees on the plot first — their wood drops for the haulers. Any uneven ground is levelled next, then the work proper begins.'
              : '🪏 Preparing the site: the crew is levelling the ground — cutting the high side, packing up the low side. Materials arrive once the plot sits flat.',
          ),
        )
      }
      const dur = siteBuildTime(b.type, b.q, b.r)
      const prog = el('div', 'sel-row')
      prog.appendChild(elHtml('span', 'sel-label', itemIconSvg(b.type === 'chop' ? 'axe' : 'hammer', 15)))
      prog.appendChild(bar(b.progress / dur, 'build'))
      prog.appendChild(
        el('span', 'sel-val', Math.floor((100 * b.progress) / dur) + '%'),
      )
      panel.appendChild(prog)
      // Materials on site vs what the remaining work still needs delivered.
      const remainFrac = Math.max(0, 1 - b.progress / dur)
      let starved = false
      const matRow = el('div', 'sel-buffer')
      matRow.appendChild(el('span', 'sel-label', 'Materials:'))
      let anyCost = false
      for (const [res, c] of Object.entries(def.cost)) {
        if (!c) continue
        anyCost = true
        const have = b.buffer[res as Resource] ?? 0
        const needTotal = c * remainFrac
        if (have <= 0.01 && needTotal > 0.01) starved = true
        matRow.appendChild(
          el('span', 'chip mini', `${RES_ICON[res as Resource]} ${Math.floor(have)}/${Math.ceil(needTotal)}`),
        )
      }
      if (anyCost) panel.appendChild(matRow)
      // During site prep the empty material bar is DELIBERATE (haulers hold back until
      // the plot is clear and level) — the prep hint above explains it, so the
      // "waiting for materials" alarm stays quiet until the blueprint stage.
      if (!prepPhase) {
        // Building takes a hammer in hand — a realm with none anywhere (armory
        // or a serf's grip) will watch this site sit fully-supplied forever.
        // The workbench is the way out: any serf can raise one bare-handed.
        let noHammer = false
        if (b.owner === st.me && b.type !== 'chop' && b.type !== 'workbench' && !isTerraform(b.type)) {
          noHammer = (st.self?.items?.hammer ?? 0) <= 0
          if (noHammer) {
            for (const u of st.units.values()) {
              if (u.owner === st.me && u.person?.held === 'hammer') {
                noHammer = false
                break
              }
            }
          }
        }
        panel.appendChild(
          el(
            'div',
            'sel-hint',
            starved
              ? '⏳ Waiting for materials — serfs bring them from storage. If the stores look full, check your storehouse min reserves.'
              : b.type === 'chop'
                ? '🪓 A serf with an axe will fell this tree — the wood drops here to be hauled home.'
                : isTerraform(b.type)
                  ? '🪏 A serf with a shovel will come and work the ground.'
                  : noHammer
                    ? '⚠️ No hammer in your realm — nobody can build this. Order one at your Town Hall (any serf can whittle it).'
                    : '🔨 A serf with a hammer will come and build this.',
          ),
        )
      }
      // Supply priority: serfs serve higher-priority sites first (−3…+3).
      // Terraform orders have no material cost but still queue for shovel-serfs,
      // so they get the same knob.
      if (b.owner === st.me && (anyCost || isTerraform(b.type))) {
        const prio = b.prio ?? 0
        const row = el('div', 'sel-row prio-row')
        row.appendChild(el('span', 'sel-label', '⚑'))
        const down = el('button', 'btn prio-btn') as HTMLButtonElement
        down.textContent = '−'
        down.disabled = prio <= -3
        down.title = 'Lower supply priority'
        down.onpointerdown = (e) => {
          if (e.button === 0) tx({ t: 'prio', id: b.id, prio: prio - 1 })
        }
        const label =
          prio === 0 ? 'Normal' : prio > 0 ? `High +${prio}` : `Low ${prio}`
        const val = el('span', 'prio-val', label)
        val.title = 'Supply priority — serfs bring materials to higher sites first'
        const up = el('button', 'btn prio-btn') as HTMLButtonElement
        up.textContent = '+'
        up.disabled = prio >= 3
        up.title = 'Raise supply priority'
        up.onpointerdown = (e) => {
          if (e.button === 0) tx({ t: 'prio', id: b.id, prio: prio + 1 })
        }
        row.appendChild(down)
        row.appendChild(val)
        row.appendChild(up)
        panel.appendChild(row)
      }
      if (b.owner === st.me) appendDemolish(b)
      return
    }

    if (b.owner !== st.me || st.observer) return

    if (active === 'info' && b.paused) {
      content.appendChild(el('div', 'sel-hint', '⏸ Paused — work stopped; haulers still collect the buffer.'))
    }

    // Render only the selected tab. Other sections cannot bury its controls.

    // Crafting first: the buttons players open this panel to press.
    if (active === 'craft' && (def.forges?.length || def.trains?.length)) {
      const { body } = section('craft', '🛠️', 'Crafting', b.queue.length ? `${b.queue.length} queued` : '', content)
      // Stalled forge queues say WHY: nobody's working, or the wrong hands are.
      if (b.queue.length > 0 && (b.type === 'barracks' || b.type === 'workbench')) {
        let crafter = false
        for (const wid of b.workers) {
          const wu = st.units.get(wid)
          if (!wu || wu.state !== 'working') continue
          if (b.type !== 'barracks' || wu.person?.held === 'smithhammer') {
            crafter = true
            break
          }
        }
        if (!crafter) {
          body.appendChild(
            el(
              'div',
              'sel-hint',
              b.type === 'barracks'
                ? "⚠ The anvil stands silent — it needs a crafter wielding a smith's hammer (assign one, or craft the hammer at a Workbench)."
                : '⚠ Nobody is working the bench — orders wait until a serf staffs it.',
            ),
          )
        }
      }
      // Pay-as-you-craft: the head order pauses when the stores can't fund the
      // next tick's sliver of its cost — say so, naming what's missing.
      const head = b.queue[0]
      if (head?.item != null && st.self) {
        const idef = ITEMS[head.item]
        const missing = Object.entries(idef.forgeCost)
          .filter(([res, c]) => (c ?? 0) > 0 && (st.self!.stockpile[res as Resource] ?? 0) < (c ?? 0) / idef.forgeTime)
          .map(([res]) => RES_ICON[res as Resource])
        if (missing.length) {
          body.appendChild(
            el('div', 'sel-hint', `⏸ Crafting paused — the stores are out of ${missing.join(' ')}. Work resumes as materials come in.`),
          )
        }
      }
      appendCraftControls(b, body)
      body.appendChild(
        el(
          'div',
          'sel-hint',
          b.type === 'townhall'
            ? '🪚 Order a piece of wooden gear and a free serf comes to whittle it — the cost is paid while they work, no workbench needed.'
            : 'Forged gear lands in the town-hall armory. Select serfs and “Give an item” to arm them.',
        ),
      )
    }

    // Spare gear shares one rack. Minimums refill it as villagers take items.
    if (active === 'armory' && b.type === 'townhall' && st.self) appendArmory(b, content)

    // Crew editing has its own roomy picker, including when every slot is full.
    if (active === 'crew' && slots > 0) {
      const crew = b.crew ?? []
      const automatic = st.self?.governor?.enabled !== false && !b.manualStaffing
      const { body } = section('crew', '👥', 'Workers', `${b.workers.length}/${slots} on duty`, content)
      body.appendChild(el('p', 'sel-hint', automatic
        ? 'Your village chooses the workers. Take over to build your own crew.'
        : 'This crew is chosen by you. Compare villagers and change their posts.'))
      const manage = el('button', 'crew-manage-button')
      manage.type = 'button'; manage.dataset.buildingAction = 'manage-crew'
      manage.disabled = !st.connected
      const icon = el('span', 'crew-manage-icon', '👥'); icon.setAttribute('aria-hidden', 'true')
      const copy = el('span', 'crew-manage-copy')
      copy.append(el('strong', '', automatic ? 'Choose crew manually' : 'Manage crew'), el('span', '', 'Compare skills, tools and current jobs'))
      const arrow = el('span', 'crew-manage-arrow', '↗'); arrow.setAttribute('aria-hidden', 'true')
      manage.append(icon, copy, arrow)
      manage.onkeydown = e => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }
      manage.onkeyup = e => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }
      pressAction(manage, () => {
        if (automatic) tx({ t: 'staffing', id: b.id, automatic: false })
        st.emit('ui:assign', b.id)
      })
      body.appendChild(manage)
      const summary = el('div', 'crew-summary-slots')
      for (let i = 0; i < slots; i++) {
        const person = crew[i] === undefined ? undefined : st.units.get(crew[i])?.person
        summary.appendChild(el('span', 'crew-summary-person' + (crew[i] === undefined ? ' is-empty' : ''),
          crew[i] === undefined ? '+ Open place' : `✓ ${person?.name?.split(' ')[0] ?? '#' + crew[i]}`))
      }
      body.appendChild(summary)
    }

    // Hubs hold a TOTAL number of items (b.store is the real contents). Each
    // resource row: live qty, accept toggle, and min/max stock limits; plus a
    // storage priority (deposits fill high first, spending drains low first).
    if (active === 'storage' && (b.type === 'townhall' || b.type === 'storehouse')) {
      const cap = buildingStorage(b)
      let totalHeld = 0
      for (const res of Object.keys(RES_ICON) as Resource[]) totalHeld += b.store?.[res] ?? 0
      const full = totalHeld >= cap * 0.98
      const { body } = section('storage', '📦', 'Storage', `${Math.floor(totalHeld)}/${cap}${full ? ' ⚠' : ''}`, content)
      const row = el('div', 'sel-row')
      row.appendChild(el('span', 'sel-label', '📦'))
      row.appendChild(bar(Math.min(1, totalHeld / Math.max(1, cap)), full ? undefined : 'build'))
      row.appendChild(el('span', 'sel-val', `${Math.floor(totalHeld)}/${cap} items`))
      body.appendChild(row)

      const grid = el('div', 'store-grid')
      // Column headers: the naked min/max inputs read as noise without them.
      grid.appendChild(el('span', 'store-col', 'in store'))
      grid.appendChild(el('span', 'store-col', 'keep min'))
      grid.appendChild(el('span', 'store-col', 'cap max'))
      for (const res of Object.keys(RES_ICON) as Resource[]) {
        const qty = Math.floor(b.store?.[res] ?? 0)
        const accepted = b.accept?.[res] !== false
        const lim = b.lim?.[res]

        const acc = el('button', 'store-acc' + (accepted ? '' : ' off'), `${RES_ICON[res]} ${qty}`) as HTMLButtonElement
        acc.title = accepted ? 'Accepting — click to refuse ' + res : 'Refusing ' + res + ' — click to accept'
        acc.addEventListener('pointerdown', (e) => {
          e.preventDefault()
          e.stopPropagation()
          tx({ t: 'store', id: b.id, res, accept: !accepted })
        })
        grid.appendChild(acc)

        const mkLim = (key: 'min' | 'max'): HTMLInputElement => {
          const inp = el('input', 'store-lim') as HTMLInputElement
          inp.type = 'number'
          inp.min = '0'
          inp.placeholder = key
          const v = lim?.[key]
          if (v !== undefined) inp.value = String(v)
          inp.title = key === 'min'
            ? 'Keep at least this many here (construction supply won\u2019t draw below it)'
            : 'Never store more than this many here'
          inp.addEventListener('change', () => {
            const n = inp.value === '' ? null : Math.max(0, Math.round(Number(inp.value)))
            tx({ t: 'store', id: b.id, res, [key]: Number.isFinite(n as number) || n === null ? n : null })
            inp.blur()
          })
          return inp
        }
        grid.appendChild(mkLim('min'))
        grid.appendChild(mkLim('max'))
      }
      body.appendChild(grid)

      const prow = el('div', 'prio-row')
      prow.appendChild(el('span', 'sel-label', 'Storage priority'))
      const minus = el('button', 'btn btn-sm', '−') as HTMLButtonElement
      const val = el('span', 'prio-val', String(b.sprio ?? 0))
      const plus = el('button', 'btn btn-sm', '+') as HTMLButtonElement
      const sendP = (d: number): void => {
        const next = Math.max(-3, Math.min(3, (b.sprio ?? 0) + d))
        tx({ t: 'sprio', id: b.id, prio: next })
      }
      minus.addEventListener('pointerdown', (e) => { e.preventDefault(); e.stopPropagation(); sendP(-1) })
      plus.addEventListener('pointerdown', (e) => { e.preventDefault(); e.stopPropagation(); sendP(1) })
      prow.appendChild(minus)
      prow.appendChild(val)
      prow.appendChild(plus)
      body.appendChild(prow)
      body.appendChild(
        el('div', 'sel-hint', `Holds ${cap} items total. Toggle a resource to refuse it; min keeps a reserve here, max caps it.`),
      )
    }

    // Maintenance store: the daily upkeep bill is paid from here (haulers keep it
    // stocked); an empty store means the building weathers.
    if (active === 'info') {
      const bill = upkeepDaily(b.type)
      const entries = Object.entries(bill).filter(([, n]) => (n ?? 0) > 0)
      if (entries.length) {
        let dry = false
        const chips: HTMLElement[] = []
        for (const [res, daily] of entries) {
          const have = Math.floor(b.mstore?.[res as Resource] ?? 0)
          const cap = (daily ?? 0) * UPKEEP_STORE_DAYS
          if (have <= 0) dry = true
          chips.push(el('span', 'chip mini', `${RES_ICON[res as Resource]} ${have}/${cap}`))
        }
        const { body, sum } = section('upkeep', '🏚️', 'Upkeep', dry ? '⚠ empty' : 'stocked', content)
        if (dry) sum.classList.add('sec-warn')
        const row = el('div', 'sel-buffer')
        for (const c of chips) row.appendChild(c)
        body.appendChild(row)
        body.appendChild(
          el(
            'div',
            'sel-hint',
            dry
              ? '⚠ Upkeep store is empty — the building will weather until haulers restock it.'
              : 'The daily upkeep bill is paid from this store; haulers keep it filled from storage.',
          ),
        )
      }
    }

    // Buffered goods
    const buffered = Object.entries(b.buffer).filter(([, v]) => (v ?? 0) >= 1)
    if (active === 'info' && buffered.length) {
      const buf = el('div', 'sel-buffer')
      buf.appendChild(el('span', 'sel-label', 'Awaiting pickup:'))
      for (const [k, v] of buffered) {
        buf.appendChild(el('span', 'chip mini', RES_ICON[k as Resource] + ' ' + Math.floor(v ?? 0)))
      }
      content.appendChild(buf)
    }

    // Rally point: trainers and the hall (newborn serfs muster there).
    if (active === 'info' && (def.trains?.length || b.type === 'townhall')) {
      content.appendChild(
        el(
          'div',
          'sel-hint',
          b.rally
            ? '⚑ Rally: ' + b.rally.q + ',' + b.rally.r + ' — right-click map to move it. Newborn serfs muster there.'
            : '⚑ Right-click the map to set a rally point for newcomers.',
        ),
      )
    }

    appendDemolish(b)
  }

  /** The headline stat for a producer: current output and a full-crew baseline. */
  function productionCard(b: Building, slots: number): HTMLElement | null {
    if (b.type === 'farm') return farmCard(st, b, slots)
    const def = BUILDINGS[b.type]
    if (!def.produces) return null
    const bonus = b.owner === st.me && b.type === 'pier' ? realmMultiplier(st.self?.realm, 'harvest', st.tick) : 1
    const perHand = b.type === 'pier' ? 2 : TOOL_GATHER_MULT
    const fmt = (v: number): string => v >= .95 ? String(Math.round(v)) : v.toFixed(1)
    const card = el('div', 'sel-yield' + (b.paused ? ' paused' : ''))
    let any = false
    for (const [res, rate] of Object.entries(def.produces)) {
      if (!rate) continue
      any = true
      const each = rate * 60 * perHand * bonus
      const row = el('div', 'yield-row')
      row.appendChild(el('span', 'yield-ico', RES_ICON[res as Resource]))
      const fig = el('div', 'yield-fig')
      fig.appendChild(el('span', 'yield-num', b.paused ? '—' : fmt(each * b.workers.length)))
      fig.appendChild(el('span', 'yield-unit', '/min'))
      row.appendChild(fig)
      row.appendChild(el('span', 'yield-note', b.paused ? 'paused' : b.workers.length >= slots ? 'full output' : `→ ${fmt(each * slots)} at full crew`))
      card.appendChild(row)
    }
    if (bonus > 1) card.appendChild(el('div', 'yield-sub', `✦ Village improvements: +${Math.round((bonus - 1) * 100)}% output`))
    return any ? card : null
  }

  function appendArmory(b: Building, host: HTMLElement): void {
    const total = Object.values(st.self?.items ?? {}).reduce((sum, n) => sum + (n ?? 0), 0)
    const minimums = armoryMinimums()
    const allocated = armoryItems.reduce((sum, item) => sum + (minimums[item] ?? 0), 0)
    const { body } = section('armory', '🛡️', 'Armory', `${total}/${ARMORY_CAP} stored`, host)
    const summary = el('div', 'armory-summary')
    for (const [label, count] of [['On the rack', total], ['Minimums assigned', allocated]] as const) {
      const card = el('div', 'armory-meter' + (count >= ARMORY_CAP ? ' full' : ''))
      card.append(el('span', '', label), el('strong', '', `${count} / ${ARMORY_CAP}`))
      const meter = el('progress', '')
      meter.max = ARMORY_CAP
      meter.value = count
      meter.setAttribute('aria-label', label)
      card.appendChild(meter)
      summary.appendChild(card)
    }
    body.appendChild(summary)
    body.appendChild(el('p', 'armory-description', 'Keep spare tools and weapons ready. Villagers automatically craft replacements when stock falls below a minimum. Gear in their hands uses no rack space.'))
    const allocationNote = el('div', 'armory-allocation-note', allocated >= ARMORY_CAP
      ? 'All 15 minimum slots assigned. Lower one to increase another.'
      : `${ARMORY_CAP - allocated} minimum ${ARMORY_CAP - allocated === 1 ? 'slot' : 'slots'} left · 0 = no spare reserve`)
    allocationNote.id = `armory-${b.id}-allocation`
    body.appendChild(allocationNote)

    const forges = [...st.buildings.values()].filter(forge => forge.owner === st.me && BUILDINGS[forge.type].forges?.length)
    let previousKind: string | undefined
    for (const item of armoryItems) {
      const def = ITEMS[item]
      if (def.kind !== previousKind) {
        body.appendChild(el('div', 'sel-group-cap armory-kind', def.kind === 'tool' ? 'Tools' : 'Weapons'))
        previousKind = def.kind
      }
      const have = st.self?.items?.[item] ?? 0
      const min = minimums[item] ?? 0
      const max = ARMORY_CAP - allocated + min
      const queued = forges.reduce((sum, forge) => sum + forge.queue.filter(order => order.item === item).length, 0)
      const waiting = forges.reduce((sum, forge) => sum + forge.queue.filter(order => order.item === item && order.ticksLeft <= 0).length, 0)
      const needsMore = have < min
      let status = waiting ? `${waiting} waiting for rack space` : queued ? `${queued} being crafted` : min === 0 ? 'No spare minimum' : needsMore ? 'Restocking needed' : 'Minimum met'
      if (needsMore && !queued) {
        const capable = forges.filter(forge => BUILDINGS[forge.type].forges?.includes(item))
        const ready = capable.some(forge => forge.done && !forge.paused)
        if (!ready) {
          const workshop = Object.values(BUILDINGS).find(building => building.forges?.includes(item))?.name ?? 'workshop'
          status = capable.some(forge => forge.done) ? 'Resume the workshop' : `Needs ${workshop}`
        } else if (total >= ARMORY_CAP) status = 'Rack full · equip villagers'
        else {
          const missing = Object.entries(def.forgeCost)
            .filter(([res, n]) => (st.self?.stockpile[res as Resource] ?? 0) < (n ?? 0))
            .map(([res]) => res)
          if (missing.length) status = `Needs ${missing.join(' + ')}`
        }
      }
      const row = el('div', 'armory-item' + (needsMore ? ' below-minimum' : ''))
      row.appendChild(elHtml('span', 'armory-item-icon', itemIconSvg(item, 27)))
      const detail = el('div', 'armory-item-detail')
      detail.append(el('strong', '', def.name), el('span', 'armory-item-status', status))
      row.appendChild(detail)
      const stock = el('div', 'armory-item-stock')
      stock.append(el('strong', '', String(have)), el('span', '', 'stored'))
      stock.setAttribute('aria-label', `${have} ${def.name} stored`)
      row.appendChild(stock)
      const stepper = el('div', 'armory-stepper')
      const label = el('label', 'armory-min-label', 'Keep min')
      label.htmlFor = `armory-${b.id}-${item}-minimum`
      stepper.appendChild(label)
      const controls = el('div', 'armory-stepper-controls')
      const input = el('input', 'armory-min-input')
      input.id = label.htmlFor
      input.type = 'number'
      input.inputMode = 'numeric'
      input.min = '0'
      input.max = String(max)
      input.step = '1'
      input.value = String(min)
      input.disabled = !canSetArmory(b)
      input.dataset.armoryControl = `${item}-minimum`
      input.setAttribute('aria-label', `${def.name} minimum`)
      input.setAttribute('aria-describedby', allocationNote.id)
      input.oninput = () => input.setCustomValidity('')
      const commit = (): void => {
        const value = Number(input.value)
        if (input.value === '' || !Number.isInteger(value) || value < 0) {
          input.setCustomValidity('Enter a whole number from 0 to 15.')
          input.reportValidity()
          return
        }
        if (!setArmoryMinimum(b, item, value)) {
          const live = armoryMinimums()
          const room = ARMORY_CAP - armoryItems.reduce((sum, it) => sum + (it === item ? 0 : live[it] ?? 0), 0)
          input.setCustomValidity(canSetArmory(b) ? `This minimum can be at most ${room}. All minimums must fit in 15 slots.` : 'Reconnect to edit armory minimums.')
          input.reportValidity()
        }
      }
      input.onchange = commit
      input.onkeydown = e => {
        e.stopPropagation()
        if (e.key === 'Enter') { e.preventDefault(); commit() }
        else if (e.key === 'Escape') {
          e.preventDefault()
          input.value = String(armoryMinimums()[item] ?? 0)
          input.setCustomValidity('')
          input.blur()
        }
      }
      const step = (delta: number): HTMLButtonElement => {
        const button = el('button', 'armory-step', delta < 0 ? '−' : '+')
        button.type = 'button'
        button.dataset.armoryControl = `${item}-${delta < 0 ? 'decrease' : 'increase'}`
        button.setAttribute('aria-label', `${delta < 0 ? 'Decrease' : 'Increase'} ${def.name} minimum`)
        // Enter/Space belong to the focused control, not the game's chat/pause
        // shortcuts. Leave the button's native keyboard click intact.
        button.onkeydown = e => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }
        button.onkeyup = e => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }
        button.disabled = !canSetArmory(b) || (delta < 0 ? min <= 0 : allocated >= ARMORY_CAP)
        if (delta > 0 && allocated >= ARMORY_CAP) button.title = 'Lower another minimum to free a slot.'
        pressAction(button, () => {
          button.focus({ preventScroll: true })
          setArmoryMinimum(b, item, (armoryMinimums()[item] ?? 0) + delta)
          // Blurring a different edited minimum can itself rebuild the panel.
          // Restore this stepper's focus even when its old button was detached.
          const live = panel.querySelector<HTMLButtonElement>(`[data-armory-control="${button.dataset.armoryControl}"]`)
          const target = live?.disabled ? live.parentElement?.querySelector<HTMLInputElement>('input') : live
          target?.focus({ preventScroll: true })
        })
        return button
      }
      controls.append(step(-1), input, step(1))
      stepper.appendChild(controls)
      row.appendChild(stepper)
      body.appendChild(row)
    }
    body.appendChild(el('p', 'sel-hint', !st.connected
      ? 'Disconnected — reconnect to change minimums.'
      : 'Restocking needs materials, the right workshop and a free worker. Select villagers and “Give an item” to equip gear from the rack.'))
  }

  /** Train + forge buttons and the shared order queue — the body of the
   *  Crafting section. */
  function appendCraftControls(b: Building, host: HTMLElement): void {
    const def = BUILDINGS[b.type]
    // Train buttons (NPC/legacy — player buildings forge gear instead of units)
    if (def.trains && def.trains.length) {
      const trainRow = el('div', 'sel-train')
      for (const ut of def.trains) {
        const udef = UNITS[ut]
        const btn = el('button', 'btn train-btn')
        btn.appendChild(el('span', 'train-icon', UNIT_ICON[ut]))
        btn.appendChild(el('span', 'train-name', udef.name))
        btn.appendChild(el('span', 'train-cost', fmtCost(udef.cost)))
        const full = b.queue.length >= 5
        btn.disabled = full || !canAfford(st, udef.cost)
        btn.title = full
          ? 'Queue is full (5)'
          : udef.name + ' — ' + fmtCost(udef.cost) + ' · ⏳ ' + (udef.trainTime ?? 0) + 's'
        btn.onclick = () => tx({ t: 'train', buildingId: b.id, unit: ut })
        trainRow.appendChild(btn)
      }
      host.appendChild(trainRow)
    }

    // Forge buttons — orders land in the town-hall armory.
    if (def.forges && def.forges.length) {
      wireCraftTip()
      const stocked = Object.values(st.self?.items ?? {}).reduce((a, v) => a + (v ?? 0), 0)
      const queued = [...st.buildings.values()].filter(forge => forge.owner === st.me)
        .reduce((sum, forge) => sum + forge.queue.filter(order => order.item != null).length, 0)
      const armoryFull = stocked + queued >= ARMORY_CAP
      const forgeRow = el('div', 'sel-train')
      for (const it of def.forges) {
        const idef = ITEMS[it]
        const btn = el('button', 'btn train-btn')
        btn.appendChild(elHtml('span', 'train-icon', itemIconSvg(it)))
        btn.appendChild(el('span', 'train-name', idef.name))
        btn.appendChild(el('span', 'train-cost', fmtCost(idef.forgeCost)))
        const full = b.queue.length >= 5
        // No affordability gate: the cost is paid tick by tick WHILE the order
        // is crafted, and short stores pause the work — queueing ahead of
        // income is a feature, not an error.
        btn.disabled = full || armoryFull
        // Hover card (what's this FOR?) — the native title would double up on it.
        btn.dataset.craftItem = it
        if (full) btn.dataset.craftStatus = 'Forge queue is full (5)'
        else if (armoryFull) btn.dataset.craftStatus = 'Armory full — arm your serfs before forging more'
        pressAction(btn, () => {
          tx({ t: 'forge', buildingId: b.id, item: it })
          // Optimistic echo: the order appears in the queue NOW — the server's
          // authoritative diff lands within a tick and replaces this. (No cost
          // echo: materials are consumed gradually as the order is worked.)
          b.queue.push({ item: it, ticksLeft: idef.forgeTime })
          refresh(true)
        })
        forgeRow.appendChild(btn)
      }
      host.appendChild(forgeRow)
    }

    // Queue — pending units or forged gear.
    if ((def.trains?.length || def.forges?.length) && b.queue.length) {
      const qWrap = el('div', 'sel-queue')
      b.queue.forEach((o, i) => {
        const row = el('div', 'queue-item')
        const qIcon = el('span', 'q-icon')
        if (o.item != null) qIcon.innerHTML = itemIconSvg(o.item, 15)
        else qIcon.textContent = o.unit != null ? UNIT_ICON[o.unit] : '•'
        row.appendChild(qIcon)
        const tt = o.item != null ? ITEMS[o.item].forgeTime : o.unit != null ? (UNITS[o.unit].trainTime ?? 1) : 1
        if (i === 0) {
          row.appendChild(bar(1 - o.ticksLeft / tt, 'train'))
          row.appendChild(el('span', 'sel-val', Math.ceil(Math.max(0, o.ticksLeft) / (o.item && b.owner === st.me ? realmMultiplier(st.self?.realm, 'craft', st.tick) : 1)) + 's of work'))
        } else {
          row.appendChild(el('span', 'q-waiting', 'queued'))
        }
        qWrap.appendChild(row)
      })
      host.appendChild(qWrap)
    }
  }

  /** ⏸/▶ toggle for buildings that produce, train or forge. Pausing stops the
   *  work and disperses the workers; haulers still collect the buffer. */
  function appendPauseToggle(b: Building, row: HTMLElement): void {
    const def = BUILDINGS[b.type]
    if (!def.produces && !def.trains?.length && !def.forges?.length) return
    const btn = el('button', 'btn' + (b.paused ? ' armed' : ''))
    btn.dataset.buildingAction = 'pause'
    btn.textContent = b.paused ? '▶ Resume work' : '⏸ Pause work'
    btn.title = b.paused
      ? 'Workers will return and production resumes'
      : 'Stop production so haulers can empty the buffer; workers find other jobs'
    pressAction(btn, () => {
      tx({ t: 'pause', id: b.id, paused: !b.paused })
    })
    row.appendChild(btn)
  }

  function appendDemolish(b: Building): void {
    const row = el('div', 'sel-actions')
    if (b.done && b.owner === st.me) appendPauseToggle(b, row)
    const armed = demolishArmedFor === b.id && Date.now() - demolishArmedAt < 5000
    // Expired arm: reset the state so it always matches what the button shows.
    if (!armed && demolishArmedFor === b.id) demolishArmedFor = -1
    const btn = el('button', 'btn btn-danger' + (armed ? ' armed' : ''))
    // An unfinished site CANCELS rather than demolishes: the works stop where they
    // stand (felled trees and regraded ground stay as they are), and everything on
    // the plot — delivered materials, banked spoil — heaps up for the haulers.
    btn.textContent = armed ? (b.done ? 'Confirm demolish?' : 'Confirm cancel?') : b.done ? '🔥 Demolish' : '✕ Cancel works'
    btn.title = b.done
      ? 'Tear down and recover ' + Math.round(DEMOLISH_REFUND * 100) + '% of the cost'
      : 'Stop the works at any stage — delivered materials and spoil drop here for the haulers; ground already cleared stays as it is'
    // pointerdown, not click: producing buildings re-render the panel every tick
    // (replaceChildren), and a click whose press spans that swap lands on a detached
    // node and silently vanishes. pointerdown fires on the pressed element at once.
    // The render-time `armed` closure decides the action, so the button always does
    // exactly what its label says — no silent re-arm after the window lapses.
    btn.onpointerdown = (e) => {
      if (e.button !== 0) return
      if (armed) {
        tx({ t: 'demolish', id: b.id })
        demolishArmedFor = -1
      } else {
        demolishArmedFor = b.id
        demolishArmedAt = Date.now()
      }
      refresh()
    }
    row.appendChild(btn)
    panel.appendChild(row)
  }

  function renderUnits(units: UnitWire[]): void {
    panel.classList.remove('building-panel')
    panel.replaceChildren()
    panel.classList.remove('hidden')

    // A single person gets a full character sheet.
    if (units.length === 1 && units[0].type === 'citizen' && units[0].person) {
      renderPerson(units[0])
      return
    }

    const head = el('div', 'sel-head')
    head.appendChild(el('span', 'sel-icon', '⚑'))
    const title = el('div', 'sel-title')
    title.appendChild(
      el('div', 'sel-name', units.length === 1 ? UNITS[units[0].type].name : units.length + ' selected'),
    )
    title.appendChild(el('div', 'sel-sub', 'Right-click to move · on an enemy to attack'))
    head.appendChild(title)
    panel.appendChild(head)

    // Group people by the item they hold (empty hands is its own group).
    const groups = new Map<string, { label: string; icon: string; n: number; hp: number; max: number }>()
    for (const u of units) {
      let key: string, label: string, icon: string
      if (u.type === 'citizen' && u.person) {
        const h = u.person.held
        key = h ?? 'empty'
        label = h ? ITEMS[h].name : 'Empty-handed'
        icon = h ? itemIconSvg(h) : '✋'
      } else {
        key = u.type
        label = UNITS[u.type].name
        icon = UNIT_ICON[u.type]
      }
      const g = groups.get(key) ?? { label, icon, n: 0, hp: 0, max: 0 }
      g.n++
      g.hp += u.hp
      g.max += unitMaxHp(u)
      groups.set(key, g)
    }
    const order = [...groups.values()].sort((a, b) => b.n - a.n)
    for (const g of order) {
      const row = el('div', 'sel-row unit-group')
      row.appendChild(elHtml('span', 'sel-label', g.icon))
      row.appendChild(el('span', 'ug-name', g.label + (g.n > 1 ? ' ×' + g.n : '')))
      row.appendChild(bar(g.hp / g.max))
      row.appendChild(el('span', 'sel-val', Math.ceil(g.hp) + '/' + Math.round(g.max)))
      panel.appendChild(row)
    }

    // What the group is up to, tallied: "3 farming · 2 on the move · 1 idle".
    const acts = new Map<string, number>()
    for (const u of units) {
      const a = activityOf(u)
      acts.set(a, (acts.get(a) ?? 0) + 1)
    }
    const tally = [...acts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([a, n]) => (units.length > 1 ? `${n} ${a}` : a))
      .join(' · ')
    panel.appendChild(el('div', 'sel-hint act-tally', '⚙ ' + tally))

    // Hand items from the armory to everyone selected.
    const people = units.filter((u) => u.owner === st.me && u.type === 'citizen' && u.person && !u.hauler && u.hp > 0)
    if (people.length && !st.observer) panel.appendChild(exploreControls(people))
    if (people.length) panel.appendChild(equipControls(people.map((u) => u.id)))

    if (people.length) {
      panel.appendChild(scavToggle(people))
      panel.appendChild(defendToggle(people))
      const armed = people.filter((u) => !!u.person && isArmed(u.person))
      if (armed.length) panel.appendChild(patrolToggle(armed))
    }
  }

  const pendingExploration = new Map<number, { on: boolean; at: number }>()
  st.on('error', () => {
    if (!pendingExploration.size) return
    pendingExploration.clear(); refresh()
  })
  /** Direct exploration orders leave routine work, without changing local unit state. */
  function exploreControls(people: UnitWire[]): HTMLElement {
    const box = el('div', 'unit-explore-controls')
    const group = people.filter(u => u.owner === st.me && u.type === 'citizen' && !u.hauler && u.hp > 0)
    for (const u of group) {
      const request = pendingExploration.get(u.id)
      if (request && (!!u.exploring === request.on || Date.now() - request.at >= 4000)) pendingExploration.delete(u.id)
    }
    const waiting = group.some(u => pendingExploration.has(u.id))
    const headingOut = group.filter(u => !u.exploring), comingHome = group.filter(u => u.exploring)
    const order = (ids: number[], on: boolean): void => {
      if (!st.connected || st.observer || !ids.length || ids.some(id => pendingExploration.has(id))) return
      if (ids.some(id => {
        const u = st.units.get(id)
        return !u || u.owner !== st.me || u.type !== 'citizen' || u.hauler || u.hp <= 0 || !!u.exploring === on
      })) return
      const request = { on, at: Date.now() }
      for (const id of ids) pendingExploration.set(id, request)
      tx({ t: 'explore', ids, on })
      refresh()
      setTimeout(() => {
        let changed = false
        for (const id of ids) if (pendingExploration.get(id) === request) { pendingExploration.delete(id); changed = true }
        if (changed) refresh()
      }, 4000)
    }
    const button = (label: string, selected: UnitWire[], on: boolean): void => {
      const control = el('button', 'btn', label)
      control.dataset.exploreUnits = on ? 'send' : 'return'
      control.disabled = waiting || !st.connected
      const send = (): void => order(selected.map(u => u.id), on)
      control.onpointerdown = event => { if (event.button === 0 && !control.disabled) { event.preventDefault(); send() } }
      control.onclick = send
      box.append(control)
    }
    if (headingOut.length) button(`🧭 Explore${group.length > 1 ? ` (${headingOut.length})` : ''}`, headingOut, true)
    if (comingHome.length) button(`⌂ Return to village${group.length > 1 ? ` (${comingHome.length})` : ''}`, comingHome, false)
    box.append(el('p', 'sel-hint', waiting ? 'Waiting for your order to be confirmed…'
      : comingHome.length === group.length ? 'Wandering to reveal terrain. Return to village ends exploration.'
      : 'Leave routine work to wander and reveal terrain. You can bring them home at any time.'))
    return box
  }

  /** 🥾 idle patrol for armed serfs: amble around the post, or stand rooted. */
  function patrolToggle(people: UnitWire[]): HTMLElement {
    const allOn = people.every((u) => u.noPatrol !== true)
    const btn = el('button', 'btn' + (allOn ? '' : ' armed'))
    btn.textContent = allOn ? '🥾 Patrol: on' : '🥾 Patrol: off'
    btn.title = allOn
      ? 'Ambles around their post while idle, eyes open — click to root them in place'
      : 'Stands watch exactly where posted — click to let them patrol their surroundings'
    // pointerdown: per-tick panel re-renders eat plain clicks (same as pause).
    btn.onpointerdown = (e) => {
      if (e.button !== 0) return
      for (const u of people) tx({ t: 'patrol', id: u.id, on: !allOn })
    }
    return btn
  }

  /** ⚔/🏃 combat stance: fight back when attacked, or always flee. One button
   *  covers the given serfs (a mixed group is set to fight first). */
  function defendToggle(people: UnitWire[]): HTMLElement {
    const stands = (u: UnitWire): boolean => u.defend !== false
    const allFight = people.every(stands)
    const btn = el('button', 'btn')
    btn.textContent = allFight ? '⚔ If attacked: fight back' : '🏃 If attacked: flee'
    btn.title = allFight
      ? 'Stands their ground when hurt (bare fists if need be) — click to make them run for home instead'
      : 'Runs for home when hurt and never picks fights — click to make them stand their ground'
    // pointerdown: per-tick panel re-renders eat plain clicks (same as pause).
    btn.onpointerdown = (e) => {
      if (e.button !== 0) return
      for (const u of people) tx({ t: 'defend', id: u.id, fight: !allFight })
    }
    return btn
  }

  /** 🌲 per-serf scavenging opt-in (off by default). One button covers the given
   *  serfs (a mixed group turns everyone on first). */
  function scavToggle(people: UnitWire[]): HTMLElement {
    const allOn = people.every((u) => u.scavOn === true)
    const btn = el('button', 'btn' + (allOn ? '' : ' armed'))
    btn.textContent = allOn ? '🌲 Scavenging: on' : '🌲 Scavenging: off'
    btn.title = allOn
      ? 'Combs the wilds for wood & stone when idle — click to stop it'
      : 'Not gathering from the wilds (the default) — click to let them scavenge when idle'
    // pointerdown: per-tick panel re-renders eat plain clicks (same as pause).
    btn.onpointerdown = (e) => {
      if (e.button !== 0) return
      for (const u of people) tx({ t: 'scav', id: u.id, on: !allOn })
    }
    return btn
  }

  // Optimistic gear-change markers, keyed by unit id: a person is "locked in" to a
  // swap the instant you click — before the server's errand echoes back — so there's
  // immediate feedback and no clicking twice. Cleared once the real errand takes over,
  // the swap lands, or (on a rejection) a short timeout elapses.
  const pendingEquip = new Map<number, { item: ItemType | null; at: number }>()
  function equipPending(id: number): boolean {
    const e = pendingEquip.get(id)
    if (!e) return false
    const u = st.units.get(id)
    if (!u) { pendingEquip.delete(id); return false }
    if (u.errand) return true // server took it — the real errand now drives the lock
    if ((u.person?.held ?? null) === e.item) { pendingEquip.delete(id); return false } // already done
    if (Date.now() - e.at > 2500) { pendingEquip.delete(id); return false } // rejected / timed out
    return true // optimistic window before the ack
  }
  function markEquip(ids: number[], item: ItemType | null): void {
    const at = Date.now()
    for (const id of ids) pendingEquip.set(id, { item, at })
    tx({ t: 'equip', ids, item })
    refresh() // re-render right now so the lock + note appear on the same click
  }

  /** Buttons to fetch an armory item for the given people (they walk to the town hall). */
  function equipControls(ids: number[]): HTMLElement {
    const wrap = el('div', 'equip-box')
    const items = st.self?.items ?? {}
    const reserved = st.self?.reserved ?? {}
    const total = (Object.values(items) as number[]).reduce((a, b) => a + (b ?? 0), 0)
    wrap.appendChild(el('div', 'sel-group-cap', `Give an item · armory ${total}/${ARMORY_CAP}`))

    // People in this selection mid-change: a real errand, or an optimistic just-clicked one.
    const erranding = ids.filter((id) => !!st.units.get(id)?.errand || equipPending(id))
    // A single person mid-change locks their choices until it finishes (or they cancel).
    const locked = ids.length === 1 && erranding.length === 1
    // free = on the rack minus what's already promised to people mid-swap, so you can
    // never hand out more than exist.
    const free = (it: ItemType) => (items[it] ?? 0) - (reserved[it] ?? 0)

    const grid = el('div', 'equip-grid')
    const avail = (Object.keys(items) as ItemType[]).filter((it) => free(it) > 0)
    for (const it of avail) {
      const b = elHtml('button', 'equip-btn', `${itemIconSvg(it, 15)} ${ITEMS[it].name}`)
      b.appendChild(el('span', 'equip-n', String(free(it))))
      if (locked) b.disabled = true
      else b.onclick = () => markEquip(ids, it)
      grid.appendChild(b)
    }
    if (avail.length === 0) grid.appendChild(el('div', 'sel-hint', 'Armory empty — forge gear at the Smithy.'))
    wrap.appendChild(grid)

    const empty = el('button', 'equip-btn empty', '✋ Empty their hands') as HTMLButtonElement
    if (locked) empty.disabled = true
    else empty.onclick = () => markEquip(ids, null)
    wrap.appendChild(empty)

    if (erranding.length > 0) {
      const label = erranding.length > 1 ? `✕ Cancel gear change (${erranding.length})` : '✕ Cancel gear change'
      const cancel = el('button', 'equip-btn cancel', label)
      cancel.onclick = () => {
        for (const id of erranding) pendingEquip.delete(id)
        tx({ t: 'equipCancel', ids: erranding })
        refresh()
      }
      wrap.appendChild(cancel)
      wrap.appendChild(el('div', 'sel-hint', locked ? 'Swapping gear — cancel to stop.' : 'Some are swapping — cancel to call them back.'))
    } else {
      wrap.appendChild(el('div', 'sel-hint', 'They walk to the town-hall armory to swap.'))
    }
    return wrap
  }

  /** What an unarmed person is doing right now, from their state/job. */
  const activityOf = (u: UnitWire): string => unitActivity(st, u)

  /** Full character sheet for a single person: attributes, what they hold, skills. */
  function renderPerson(u: UnitWire): void {
    const p = u.person!
    const held = p.held
    const armed = isArmed(p)
    const head = el('div', 'sel-head')
    head.appendChild(held ? elHtml('span', 'sel-icon', itemIconSvg(held, 24)) : el('span', 'sel-icon', '🧑'))
    const title = el('div', 'sel-title')
    title.appendChild(nameTitle('u', u.id, p.name, p.name, u.owner === st.me))
    const vet = personTitle(p)
    const sub =
      (u.owner === st.me ? `age ${ageYears(p)}` : `age ${ageYears(p)} · ${nameOf(st, u.owner)}${st.factions.get(u.owner)?.controller === 'neutral' ? ' · Neutral village · AI' : ''}`) +
      (vet ? ` · ${vet}` : '')
    title.appendChild(el('div', 'sel-sub', sub))
    head.appendChild(title)
    panel.appendChild(head)

    // What they're doing right now — front and centre.
    const act = activityOf(u)
    const actRow = el('div', 'sel-row act-row')
    actRow.appendChild(el('span', 'sel-label', '⚙'))
    actRow.appendChild(el('span', 'act-txt', act.charAt(0).toUpperCase() + act.slice(1)))
    panel.appendChild(actRow)
    if (u.raidLoot && hasGoods(u)) {
      const loot = el('div', 'sheet-group')
      loot.appendChild(el('div', 'sel-group-cap', u.raidLoot === st.me ? 'Your stolen supplies' : `Supplies stolen from ${nameOf(st, u.raidLoot)}`))
      for (const [res, amount] of Object.entries(u.carrying ?? {})) {
        if (!amount || amount <= 0) continue
        const row = el('div', 'sel-row')
        row.appendChild(el('span', 'sel-label', RES_ICON[res as Resource]))
        const label = el('span', 'skill-nm', res.charAt(0).toUpperCase() + res.slice(1))
        label.style.flex = '1'
        label.style.minWidth = '0'
        row.appendChild(label)
        row.appendChild(el('span', 'sel-val', String(Math.round(amount))))
        loot.appendChild(row)
      }
      loot.appendChild(el('div', 'sel-hint', 'Intercept before they reach home. Defeated raiders drop their cargo.'))
      panel.appendChild(loot)
    }
    if (u.owner === st.me && !st.observer && !u.hauler && u.hp > 0) panel.appendChild(exploreControls([u]))

    // Their workplace: the building they're assigned (or crew-pinned) to, as a
    // chip — click flies the camera there; hovering silhouettes it in the world.
    if (u.owner === st.me) {
      wireJobHoverDelegate(st)
      let jb = u.job !== undefined ? st.buildings.get(u.job) : undefined
      if (!jb) for (const b of st.buildings.values()) { if (b.owner === st.me && b.crew?.includes(u.id)) { jb = b; break } }
      if (jb) {
        const wb = jb
        const row = el('div', 'sel-row job-row')
        row.appendChild(el('span', 'sel-label', '⚒'))
        const chip = el('button', 'job-chip') as HTMLButtonElement
        chip.dataset.jobB = String(wb.id)
        chip.appendChild(el('span', 'job-chip-ico', BUILD_ICON[wb.type]))
        chip.appendChild(el('span', 'job-chip-name', (wb.done ? wb.name || BUILDINGS[wb.type].name : `${BUILDINGS[wb.type].name} (site)`)))
        chip.title = 'Their workplace — click to view it'
        // pointerdown: the panel re-renders every tick and eats clicks.
        chip.addEventListener('pointerdown', (e) => {
          if (e.button !== 0) return
          e.preventDefault()
          e.stopPropagation()
          jumpTo(wb.q, wb.r)
        })
        row.appendChild(chip)
        panel.appendChild(row)
      }
    }

    // Working a producer: show THEIR personal gather rate at this spot.
    if (u.state === 'working' && u.job !== undefined) {
      const jb = st.buildings.get(u.job)
      const produces = jb && BUILDINGS[jb.type].produces
      if (jb && produces) {
        // Their personal pace: right tool at full speed × trade skill; the land's
        // multiplier (fertile fields, bordering forest) scales the base rate.
        const proper = p.held === requiredTool(jb.type)
        const pace = jb.type === 'farm'
          ? (proper ? 2 : 1) * farmSkillMult(p.skill.farm ?? 0)
          : (proper ? TOOL_GATHER_MULT : BARE_HAND_MULT) * skillMult(p.skill[jobSkill(jb.type)] ?? 0)
        const tmult = jb.type === 'farm'
          ? farmYieldMult(buildingFootprint('farm', jb.q, jb.r, jb.dir).map((c) => terrainAt(st, c.q, c.r)))
          : 1
        const bonus = jb.type === 'farm' && jb.owner === st.me ? realmMultiplier(st.self?.realm, 'harvest', st.tick) : 1
        const parts: string[] = []
        for (const [res, rate] of Object.entries(produces)) {
          if (!rate) continue
          const perMin = rate * 60 * pace * tmult * bonus
          const rateLabel = jb.type === 'farm' ? String(Number(perMin.toFixed(1))) : perMin >= 0.95 ? String(Math.round(perMin)) : perMin.toFixed(1)
          parts.push(`${RES_ICON[res as Resource]} ${rateLabel}/min`)
        }
        parts.push(`×${pace.toFixed(1)} ${proper && p.held ? ITEMS[p.held].name.toLowerCase() : 'bare hands'}`)
        if (jb.type === 'farm') parts.push(`${Math.round(farmSkillMult(p.skill.farm ?? 0) * 100)}% skill output`)
        if (parts.length) {
          const rateRow = el('div', 'sel-hint', `Gathering ${parts.join(' · ')}${!proper ? ` — equip a ${requiredTool(jb.type)} to speed up` : ''}`)
          panel.appendChild(rateRow)
        }
      }
    }

    // What's actually in their hands — a real item, or nothing (bare fists).
    const kind = held ? (armed ? 'armed' : 'tool') : 'empty'
    const hold = el('div', 'hold-row ' + kind)
    hold.appendChild(held ? elHtml('span', 'hold-ico', itemIconSvg(held, 21)) : el('span', 'hold-ico', '✊'))
    const htxt = el('div', 'hold-txt')
    if (held && armed) {
      const c = personCombat(p)
      htxt.appendChild(el('span', 'hold-nm', ITEMS[held].name))
      htxt.appendChild(el('span', 'hold-sub', `⚔ ${c.atk} · ${c.range > 1 ? 'range ' + c.range : 'melee'}`))
    } else if (held) {
      htxt.appendChild(el('span', 'hold-nm', ITEMS[held].name))
      htxt.appendChild(el('span', 'hold-sub', activityOf(u)))
    } else {
      htxt.appendChild(el('span', 'hold-nm', 'Bare hands'))
      htxt.appendChild(el('span', 'hold-sub', 'no item — fights with fists'))
    }
    hold.appendChild(htxt)
    panel.appendChild(hold)

    // Pending armory trip: they keep what they hold until the swap resolves.
    if (u.state === 'errand' && u.errand) {
      const to = u.errand.to
      const what = to ? `${itemIconSvg(to, 14)} ${ITEMS[to].name}` : 'stow their gear'
      const swapping = u.errand.swap != null
      const note = swapping
        ? to
          ? `⚒ at the armory — depositing &amp; collecting ${what}`
          : '⚒ at the armory — stowing their gear'
        : to
          ? `🚶 walking to the armory for ${what}`
          : '🚶 walking to the armory to store their gear'
      panel.appendChild(elHtml('div', 'errand-note', note))
    } else if (u.owner === st.me && equipPending(u.id)) {
      // Just clicked — show the intent instantly, before the server's errand echoes.
      const it = pendingEquip.get(u.id)!.item
      const what = it ? `${itemIconSvg(it, 14)} ${ITEMS[it].name}` : 'empty hands'
      panel.appendChild(elHtml('div', 'errand-note', it ? `⋯ sending them for ${what}` : '⋯ sending them to stow their gear'))
    }

    // ---- Vitals: health + food, grouped so they read as one status block ----
    const max = personMaxHp(p)
    const vitals = el('div', 'sheet-group')
    const hpRow = el('div', 'sel-row')
    hpRow.appendChild(el('span', 'sel-label', '❤'))
    hpRow.appendChild(bar(u.hp / max))
    hpRow.appendChild(el('span', 'sel-val', Math.ceil(u.hp) + '/' + max))
    vitals.appendChild(hpRow)

    // food meter + satchel (auto green/amber/red by fullness)
    const food = Math.max(0, Math.min(100, p.food ?? 100))
    const foodRow = el('div', 'sel-row')
    foodRow.appendChild(el('span', 'sel-label', '🍞'))
    foodRow.appendChild(bar(food / 100))
    const starve = starvingHours(st, p)
    foodRow.appendChild(
      starve !== null
        ? el('span', 'sel-val starving', `STARVING ${starve.toFixed(1)}h`)
        : el('span', 'sel-val', Math.round(food) + '%' + (p.foodItems ? ` · 🎒${p.foodItems}` : '')),
    )
    vitals.appendChild(foodRow)
    panel.appendChild(vitals)

    // ---- Attributes: a clean stat-card grid ----
    panel.appendChild(el('div', 'sel-group-cap', 'Attributes'))
    const attrs = el('div', 'attr-row')
    for (const a of ATTR_KEYS) {
      const cell = el('div', 'attr-cell')
      cell.appendChild(el('span', 'attr-v', String(p.attr[a])))
      cell.appendChild(el('span', 'attr-k', ATTR_ABBR[a]))
      attrs.appendChild(cell)
    }
    panel.appendChild(attrs)

    // ---- Skills (only the ones they've actually picked up) ----
    const skills = (Object.keys(p.skill) as SkillKey[])
      .filter((k) => p.skill[k] >= 0.1)
      .sort((a, b) => p.skill[b] - p.skill[a])
    if (skills.length) {
      panel.appendChild(el('div', 'sel-group-cap', 'Skills'))
      for (const k of skills) {
        const row = el('div', 'skill-row')
        if (k === 'farm') row.title = `${Math.round(farmSkillMult(p.skill.farm) * 100)}% farming output before tools and soil bonuses`
        row.appendChild(el('span', 'skill-nm', SKILL_LABEL[k]))
        row.appendChild(bar(p.skill[k] / 10, 'skill'))
        row.appendChild(el('span', 'skill-lv', p.skill[k].toFixed(1)))
        panel.appendChild(row)
      }
    } else {
      panel.appendChild(el('div', 'sel-group-cap', 'Skills'))
      panel.appendChild(el('div', 'ug-role', 'Untrained — they sharpen at whatever work they take up.'))
    }

    // Hand them a different item from the armory (or take back what they hold) —
    // only for your own people; a foreign person's sheet is view-only.
    if (u.owner === st.me) {
      panel.appendChild(equipControls([u.id]))
      if (u.type === 'citizen' && u.person && !u.hauler) {
        panel.appendChild(scavToggle([u]))
        panel.appendChild(defendToggle([u]))
        if (isArmed(u.person)) panel.appendChild(patrolToggle([u]))
      }
    }
  }

  function selectionScroller(): HTMLElement {
    const content = panel.querySelector<HTMLElement>('.sel-tab-content')
    return content && getComputedStyle(content).overflowY !== 'visible' ? content : panel
  }

  function refresh(force = false): void {
    // Any focused input inside the panel (rename, store min/max) survives per-tick
    // refreshes — rebuilding the DOM under a typing user eats their keystrokes.
    const ae = document.activeElement
    if (!force && ae instanceof HTMLInputElement && panel.contains(ae)) return
    // Mid-rename: keep the live <input> across per-tick refreshes; only tear it down
    // when forced (opening it) or when the selection has moved off the edited entity.
    if (editing && !force) {
      const stillSel =
        (editing.kind === 'b' && st.selection.building === editing.id) ||
        (editing.kind === 'u' && st.selection.units.length === 1 && st.selection.units[0] === editing.id)
      if (stillSel) return
      editing = null
    }
    const bId = st.selection.building
    let b: Building | undefined
    if (bId !== null && bId !== undefined) {
      b = st.buildings.get(bId)
      if (!b) st.selection.building = null
    }
    if (b) {
      const sameTab = renderedBuildingId === b.id && panel.dataset.activeBuildingTab === (buildingTabs.get(b.id) ?? 'info')
      const scrollTop = sameTab ? selectionScroller().scrollTop : 0
      let focusSelector: string | null = null
      if (renderedBuildingId === b.id && ae instanceof HTMLElement && panel.contains(ae)) {
        for (const attr of ['data-building-tab', 'data-craft-item', 'data-building-action', 'data-upgrade-hall', 'data-armory-control']) {
          const value = ae.getAttribute(attr)
          if (value !== null) { focusSelector = `[${attr}="${value}"]`; break }
        }
      }
      renderBuilding(b)
      renderedBuildingId = b.id
      const scroller = selectionScroller()
      scroller.scrollTop = scrollTop
      if (scroller !== panel) panel.scrollTop = 0
      if (focusSelector) {
        const control = panel.querySelector<HTMLElement>(focusSelector)
        const target = control instanceof HTMLButtonElement && control.disabled && control.dataset.armoryControl
          ? control.parentElement?.querySelector<HTMLInputElement>('input') : control
        target?.focus({ preventScroll: true })
      }
      return
    }
    renderedBuildingId = null
    const units = st.selection.units
      .map((id: number) => st.units.get(id))
      .filter((u: UnitWire | undefined): u is UnitWire => !!u)
    if (units.length) {
      renderUnits(units)
      return
    }
    hide()
  }

  return { refresh }
}

// ---- Diplomacy panel --------------------------------------------------------------

export interface DiploPanel {
  toggle: () => void
  refresh: () => void
  tick: () => void
  noteDiplo: (d: { from: string; to: string; state: string }) => void
  isOpen: () => boolean
}

function npcIntentText(f: FactionPublic, st: GameState): string {
  const npc = f.npc
  const target = npc?.target === st.me ? 'your village' : st.factions.get(npc?.target ?? '')?.name ?? 'a rival village'
  switch (npc?.intent) {
    case 'warning': {
      const seconds = Math.max(0, Math.ceil((npc.until ?? st.tick) - st.tick))
      const objective = npc.raidKind === 'supplies' ? `steal supplies from ${target}` : `raid ${target}`
      return seconds > 0 ? `⚠ Gathering to ${objective} · ${seconds}s` : `⚠ Preparing to ${objective}`
    }
    case 'raiding': return npc.raidKind === 'supplies' ? `⚔ Supply raid on ${target}${npc.target === st.me ? ' · defend your stores' : ''}` : `⚔ Raiding ${target}`
    case 'returning': return npc.target === st.me ? '🏴‍☠️ Carrying stolen supplies home · intercept raiders' : `🏴‍☠️ Returning with supplies from ${target}`
    case 'recovering': return '↩ Regrouping and recovering'
    case 'patrolling': return '🛡 Patrolling their borders'
    case 'expanding': return '🌿 Seeking resources and new land'
    default: return '🏕 Securing food, tools and shelter'
  }
}

function npcIsThreat(f: FactionPublic): boolean {
  return !f.ruined && (f.npc?.intent === 'warning' || f.npc?.intent === 'raiding' || f.npc?.intent === 'returning')
}

export function initDiplomacy(ctx: UICtx, onBadge: (n: number) => void): DiploPanel {
  const { st, tx, root } = ctx
  const back = el('div', 'modal-back hidden')
  const panel = el('div', 'panel modal diplo-panel')
  back.appendChild(panel)
  root.appendChild(back)

  const proposalsIn = new Set<string>()
  const proposalsOut = new Set<string>()

  function updateBadge(): void {
    const threats = [...st.factions.values()].filter(f => f.npc?.target === st.me && npcIsThreat(f)).length
    onBadge(proposalsIn.size + threats)
  }

  back.addEventListener('mousedown', (e) => {
    if (e.target === back) toggle()
  })
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape' && !back.classList.contains('hidden')) {
        e.stopPropagation()
        toggle()
      }
    },
    true,
  )

  function isOpen(): boolean {
    return !back.classList.contains('hidden')
  }

  function toggle(): void {
    back.classList.toggle('hidden')
    if (isOpen()) refresh()
  }

  function noteDiplo(d: { from: string; to: string; state: string }): void {
    const me = st.me
    if (!me || !d || !d.state) return
    if (d.state === 'proposed') {
      if (d.to === me) proposalsIn.add(d.from)
      if (d.from === me) proposalsOut.add(d.to)
    } else if (d.from === me || d.to === me) {
      const other = d.from === me ? d.to : d.from
      proposalsIn.delete(other)
      proposalsOut.delete(other)
    }
    updateBadge()
    if (isOpen()) refresh()
  }

  function actionBtn(label: string, cls: string, fn: () => void): HTMLButtonElement {
    const b = el('button', 'btn ' + cls, label)
    b.onclick = fn
    return b
  }

  function visibleLooter(owner: string): UnitWire | undefined {
    const fog = fogOf(st)
    return [...st.units.values()].find(u => u.owner === owner && u.raidLoot === st.me && u.hp > 0 &&
      hasGoods(u) && fogVisible(fog, u.q, u.r))
  }

  function refresh(): void {
    updateBadge()
    if (!isOpen()) return
    panel.replaceChildren()

    const head = el('div', 'modal-head')
    head.appendChild(el('h2', 'modal-title', '🤝 Diplomacy'))
    const close = el('button', 'btn btn-ghost modal-close', '✕')
    close.onclick = toggle
    head.appendChild(close)
    panel.appendChild(head)

    const me = st.me
    const myAllies = new Set<string>(me ? st.factions.get(me)?.allies ?? [] : [])

    const all = [...st.factions.values()]
    const players = all.filter((f: any) => f.kind === 'player' && f.controller !== 'neutral')
    const neutral = all.filter((f: any) => f.controller === 'neutral')
    neutral.sort((a, b) => Number(b.npc?.target === st.me && npcIsThreat(b)) - Number(a.npc?.target === st.me && npcIsThreat(a)) || a.name.localeCompare(b.name))
    const npcs = all.filter((f: any) => f.kind === 'npc')
    players.sort((a: any, b: any) => {
      if (a.id === me) return -1
      if (b.id === me) return 1
      if (!!a.online !== !!b.online) return a.online ? -1 : 1
      return a.name.localeCompare(b.name)
    })
    npcs.sort((a: any, b: any) => (a.tier ?? 0) - (b.tier ?? 0) || a.name.localeCompare(b.name))

    const list = el('div', 'diplo-list')

    const addRow = (f: any) => {
      const row = el('div', 'diplo-row' + (f.ruined ? ' ruined' : '') + (f.controller === 'neutral' ? ' neutral-village' : ''))
      const dot = el('span', 'fac-dot')
      dot.style.background = f.color
      row.appendChild(dot)

      const nameWrap = el('div', 'diplo-name')
      const nm = el('span', 'fac-name', f.name)
      nm.style.color = f.color
      nameWrap.appendChild(nm)
      if (f.id === me) nameWrap.appendChild(el('span', 'badge badge-you', 'YOU'))
      if (f.kind === 'player' && f.controller !== 'neutral') {
        nameWrap.appendChild(
          el('span', 'online-dot' + (f.online ? ' on' : ''), ''),
        )
      }
      if (f.ruined) nameWrap.appendChild(el('span', 'badge badge-ruin', '🏚️ ruined'))
      row.appendChild(nameWrap)

      const status = el('div', 'diplo-status')
      const actions = el('div', 'diplo-actions')

      if (f.controller === 'neutral') {
        nameWrap.appendChild(el('span', 'npc-tier', 'AI'))
        status.classList.add('npc-status')
        const stages = el('div', 'npc-stages')
        const stage = f.npc?.stage ?? 'establish'
        for (const [key, label] of [['establish', '🏕 Establish'], ['expand', '🌿 Expand'], ['contest', '⚔ Contest']]) {
          const step = el('span', 'npc-stage' + (stage === key ? ' active' : ''), label)
          if (stage === key) step.setAttribute('aria-current', 'step')
          stages.appendChild(step)
        }
        const dangerous = npcIsThreat(f)
        const intent = el('div', 'npc-intent' + (dangerous ? ' dangerous' : ''), npcIntentText(f, st))
        intent.dataset.npcFaction = f.id
        status.append(stages, intent)
        if (f.npc?.intent === 'returning' && f.npc?.target === st.me) {
          const intercept = actionBtn('⌖ Find raiders', 'btn-danger btn-sm', () => {
            // Re-check on the click: a raider can leave sight while this panel is open.
            const u = visibleLooter(f.id)
            if (!u) { refresh(); return }
            ctx.jumpTo(u.q, u.r)
            toggle()
          })
          intercept.dataset.npcIntercept = f.id
          intercept.style.alignSelf = 'flex-start'
          intercept.hidden = !visibleLooter(f.id)
          intercept.title = 'Jump to a visible raider carrying your stolen supplies'
          status.appendChild(intercept)
        }
        if (st.me && f.npc?.attitude) {
          const label = { calm: 'Calm toward you', wary: 'Wary of your expansion', hostile: 'Hostile toward you' }[f.npc.attitude as 'calm' | 'wary' | 'hostile']
          status.appendChild(el('span', 'npc-attitude ' + f.npc.attitude, label))
        }
        status.title = 'Independent villages build their economy and equipment, then raid exposed rivals for supplies. Raiders carry stolen resources home and drop them if defeated. New players have three game days before raids begin. Attacks provoke local defense.'
      } else if (f.kind === 'npc') {
        const tier = f.tier ?? 0
        const tierName = NPC_TIERS[tier]?.name ?? 'Unknown'
        status.appendChild(
          el('span', 'npc-tier', tier > 0 ? '💀'.repeat(tier) + ' ' + tierName : '🕊️ ' + tierName),
        )
      } else if (f.id !== me && me) {
        if (myAllies.has(f.id)) {
          status.appendChild(el('span', 'badge badge-ally', '⚑ Allied'))
          actions.appendChild(
            actionBtn('Break', 'btn-danger btn-sm', () => tx({ t: 'unally', to: f.id })),
          )
        } else if (proposalsIn.has(f.id)) {
          status.appendChild(el('span', 'badge badge-prop', '✉ wants to ally'))
          actions.appendChild(
            actionBtn('Accept', 'btn-gold btn-sm', () => tx({ t: 'ally', to: f.id })),
          )
          actions.appendChild(
            actionBtn('Decline', 'btn-ghost btn-sm', () => tx({ t: 'unally', to: f.id })),
          )
        } else if (proposalsOut.has(f.id)) {
          status.appendChild(el('span', 'badge badge-sent', 'proposal sent…'))
          actions.appendChild(
            actionBtn('Withdraw', 'btn-ghost btn-sm', () => tx({ t: 'unally', to: f.id })),
          )
        } else if (!f.ruined) {
          actions.appendChild(
            actionBtn('Propose', 'btn-gold btn-sm', () => tx({ t: 'ally', to: f.id })),
          )
        }
      }

      row.appendChild(status)
      if (f.home && f.id !== me) {
        const loc = actionBtn('⌖', 'btn-ghost btn-sm btn-loc', () => {
          ctx.jumpTo(f.home.q, f.home.r)
          toggle()
        })
        loc.title = 'Jump to their lands'
        actions.appendChild(loc)
      }
      row.appendChild(actions)
      list.appendChild(row)
    }

    if (players.length) list.appendChild(el('div', 'diplo-sect', 'Realms of players'))
    for (const f of players) addRow(f)
    if (neutral.length) list.appendChild(el('div', 'diplo-sect', 'Independent villages'))
    for (const f of neutral) addRow(f)
    if (npcs.length) list.appendChild(el('div', 'diplo-sect', 'Native peoples'))
    for (const f of npcs) addRow(f)
    panel.appendChild(list)

    panel.appendChild(
      el(
        'div',
        'diplo-foot',
        'Allies never auto-attack each other and share the roads in peace. Alliances need both sides to agree — and either may break them.',
      ),
    )
  }

  function tick(): void {
    if (!isOpen()) return
    for (const node of panel.querySelectorAll<HTMLElement>('[data-npc-faction]')) {
      const f = st.factions.get(node.dataset.npcFaction!)
      if (f) node.textContent = npcIntentText(f, st)
    }
    for (const node of panel.querySelectorAll<HTMLButtonElement>('[data-npc-intercept]')) {
      node.hidden = !visibleLooter(node.dataset.npcIntercept!)
    }
  }

  return { toggle, refresh, tick, noteDiplo, isOpen }
}

// ---- Help overlay --------------------------------------------------------------------

export function initHelpOverlay(ctx: UICtx): { toggle: () => void; isOpen: () => boolean } {
  const { root } = ctx
  const back = el('div', 'modal-back hidden')
  const panel = el('div', 'panel modal help-panel')
  back.appendChild(panel)
  root.appendChild(back)

  const head = el('div', 'modal-head')
  head.appendChild(el('h2', 'modal-title', '📖 How to play'))
  const close = el('button', 'btn btn-ghost modal-close', '✕')
  head.appendChild(close)
  panel.appendChild(head)

  const body = el('div', 'help-body')
  panel.appendChild(body)
  const tour = el('button', 'btn btn-gold', 'Start guided tutorial')
  tour.onclick = () => { back.classList.add('hidden'); ctx.st.emit('tutorialRestart') }
  body.appendChild(tour)

  const sect = (title: string): HTMLElement => {
    const h = el('h3', 'help-h', title)
    body.appendChild(h)
    return h
  }
  const p = (text: string): void => {
    body.appendChild(el('p', 'help-p', text))
  }

  sect('⚜ The goal')
  p(
    'Serf Land is a slow-burning realm that never sleeps. Grow a village into a kingdom: gather food, wood, stone, iron and gold, raise walls of spears, and push your borders across the endless hexes. Raiders and wild beasts prowl; native villages range from peaceful hamlets to warlord keeps worth a fortune in bounty. The world keeps ticking while you are away — leave your people well fed.',
  )

  sect('🖱 Controls')
  const table = el('table', 'help-table')
  const rows: [string, string][] = [
    ['Left click', 'Select your unit or building · place a building in build mode (Shift keeps placing)'],
    ['Double-click', 'Select every visible unit of that kind (serfs, soldiers…)'],
    ['Left drag', 'Box-select your armed folk'],
    ['Right click', 'Move selected units · attack a hostile · set rally for a selected trainer'],
    ['F', 'Centre the camera on the selection (or home)'],
    ['Wheel', 'Zoom to cursor'],
    ['MMB / Space drag · WASD / arrows', 'Pan the map'],
    ['Q / E', 'Spin the view 60° left / right'],
    ['R', 'Rotate the building you are placing (farm, pier, ramp)'],
    ['1 – 0', 'Build hotkeys'],
    ['J', 'Village Council — priorities, milestones and upgrades'],
    ['B', 'Highlight the build bar'],
    ['P', 'Diplomacy'],
    ['H', 'This scroll'],
    ['Enter', 'Chat'],
    ['Esc', 'Cancel build mode / selection · close panels'],
  ]
  for (const [k, v] of rows) {
    const tr = el('tr')
    tr.appendChild(el('td', 'help-key', k))
    tr.appendChild(el('td', 'help-what', v))
    table.appendChild(tr)
  }
  body.appendChild(table)

  sect('🏞 Land & logistics')
  p(
    'Terrain is destiny. Farms want grass or fertile plains — with two level-5 bare-handed farmers, each grass hex under their three fields yields 1 food/min, each fertile hex 1.5. Farming skill scales output: levels 0–5 produce 20%, 40%, 55%, 70%, 85% and 100%; each level beyond 5 adds 5 percentage points. Hoes double each worker’s output. For wood, hand a serf an axe and chop the forest itself (Gathering ▸ Chop Trees): drag to mark a stand, and each tree drops 10–100 wood where it stood. Replant empty grass with Gathering ▸ Plant Trees: each hex costs 1 food to plant, then the trees grow on their own for three game days. Quarries hug hills and mountains, mines must border mountains. Water is impassable; mountains are high and slow but crossable; forest and swamp are slow going.',
  )
  p(
    'Goods pile up at each workplace until serfs haul them to a town hall or storehouse. Each hub holds a set TOTAL of items (hall 500, storehouse 200). Click a hub to see its contents, refuse resources it should not take, set min reserves and max limits per good, and give it a storage priority — deposits fill high-priority depots first. Long muddy hauls strangle a kingdom: lay roads (they halve travel cost) and drop storehouses next to distant works. When the chips turn red the realm is nearly full and serfs stop collecting what cannot be banked.',
  )

  sect('🏗 Building & priorities')
  p(
    'Blueprints are free: place a site anywhere in sight and serfs walk its materials out from storage — construction consumes them on the spot and stalls without them. Select a site to raise or lower its supply priority (−3…+3); serfs and builders serve the most important build first. If no site has materials, every idle serf pitches in hauling. Pause any workplace (⏸) to let its buffer drain; demolish a site to spill its materials for recovery.',
  )

  sect('⚒ Crafting')
  p(
    "Gear comes in tiers. The Town Hall whittles the wooden gear (hammer, shovel, hoe, axe, pick, fishing rod, spear) — any free serf works its orders. The Workbench hand-crafts the same wood tier and — with stone from a quarry — the smith's hammer. The Smithy forges the metal tier (the sword, with more to come), but its anvil only rings while a crafter wielding a smith's hammer works there.",
  )

  sect('🧺 Idle hands')
  p(
    'Nobody stands around: empty-handed serfs fetch tools from the armory, pitch in bare-handed at a quarter pace where hands suffice (site clearing, trades), or scavenge the wilds for wood and stone. Raising a building takes a hammer, digging a shovel, felling an axe — no tool, no progress. Idle archers stalk deer for the larder. The 💤 chip counts truly idle folk — click it to select them.',
  )

  sect('🌫 Fog of war')
  p(
    'You see ten hexes beyond your borders and your people; allies share their sight with you. Explored land dims to memory when you leave; the deep void is uncharted, and the camera will not wander far into it.',
  )
  p('Use Explore the map to send a free villager wandering, or select a person and choose Explore. They reveal terrain as they travel, with no fixed route or expedition fee. Follow moves your camera to them; Return to village ends exploration and sends them home. Ordinary wildlife can still threaten them.')
  p(
    'People eat. Keep food flowing or they starve; keep houses rising or growth stalls. Hunt deer, cull wolves and bears for extra meat.',
  )

  sect('🤝 Diplomacy & war')
  p(
    'Press P to court the other realms. An alliance needs a proposal and an acceptance; allies never auto-attack each other. Everyone else is fair game — but towers and townhalls shoot back, and razing a raider camp or an NPC keep pays a gold bounty. Palisades block everyone on foot (leave a gap for your own folk), and beware the dark: wolves and bears hunt twice as boldly at night.',
  )

  const footer = el('div', 'help-foot', 'Press H or Esc to close')
  panel.appendChild(footer)

  function isOpen(): boolean {
    return !back.classList.contains('hidden')
  }
  function toggle(): void {
    back.classList.toggle('hidden')
  }
  close.onclick = toggle
  back.addEventListener('mousedown', (e) => {
    if (e.target === back) toggle()
  })
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape' && isOpen()) {
        e.stopPropagation()
        toggle()
      }
    },
    true,
  )

  return { toggle, isOpen }
}

// ---- What's new (changelog) ---------------------------------------------------------

export function initChangelog(ctx: UICtx): { toggle: () => void; showIfUpdated: () => void } {
  const { root } = ctx
  const back = el('div', 'modal-back hidden')
  const panel = el('div', 'panel modal help-panel')
  back.appendChild(panel)
  root.appendChild(back)
  const head = el('div', 'modal-head')
  head.appendChild(el('h2', 'modal-title', '📜 What’s new'))
  const close = el('button', 'btn btn-ghost modal-close', '✕')
  head.appendChild(close)
  panel.appendChild(head)
  const body = el('div', 'help-body')
  panel.appendChild(body)
  for (const entry of CHANGELOG.slice(0, 8)) {
    body.appendChild(el('h3', 'help-h', `${entry.title} — ${entry.date}`))
    const ul = el('ul', 'clog-list')
    for (const pt of entry.points) ul.appendChild(el('li', 'clog-pt', pt))
    body.appendChild(ul)
  }
  function toggle(): void {
    back.classList.toggle('hidden')
  }
  close.onclick = toggle
  back.addEventListener('mousedown', (e) => {
    if (e.target === back) toggle()
  })
  /** Open once after the game updates underneath the player (forced reload). */
  function showIfUpdated(): void {
    try {
      const last = localStorage.getItem('hexLastBuild')
      if (last && last !== BUILD_ID) back.classList.remove('hidden')
      localStorage.setItem('hexLastBuild', BUILD_ID)
    } catch {
      /* private mode */
    }
  }
  return { toggle, showIfUpdated }
}

// ---- Serf roster ---------------------------------------------------------------------
// A census of your people: name, age, doing, held item, food, health, best skill.
// Click a row to select them; ⌖ jumps the camera. Opened from the 👥 chip (or R).

export function initRoster(ctx: UICtx): { toggle: () => void; isOpen: () => boolean; refresh: () => void } {
  const { st, root, jumpTo } = ctx
  const back = el('div', 'modal-back hidden')
  const panel = el('div', 'panel modal roster-panel')
  back.appendChild(panel)
  root.appendChild(back)

  const head = el('div', 'modal-head')
  const title = el('h2', 'modal-title', '👥 Your people')
  head.appendChild(title)
  const close = el('button', 'btn btn-ghost modal-close', '✕')
  head.appendChild(close)
  panel.appendChild(head)
  const body = el('div', 'roster-body')
  panel.appendChild(body)

  function isOpen(): boolean {
    return !back.classList.contains('hidden')
  }
  function toggle(): void {
    back.classList.toggle('hidden')
    if (isOpen()) refresh()
  }
  close.onclick = toggle
  back.addEventListener('mousedown', (e) => {
    if (e.target === back) toggle()
  })

  const SKILLS: SkillKey[] = ['build', 'farm', 'log', 'mine', 'haul', 'craft', 'unarmed', 'spear', 'sword', 'bow']

  function refresh(): void {
    if (!isOpen()) return
    const rows: UnitWire[] = []
    for (const u of st.units.values()) {
      if (u.owner === st.me && u.type === 'citizen' && u.person && !u.hauler) rows.push(u)
    }
    rows.sort((a, b) => {
      const ai = a.state === 'idle' && !a.scav ? 0 : 1
      const bi = b.state === 'idle' && !b.scav ? 0 : 1
      return ai - bi || (a.person!.name < b.person!.name ? -1 : 1)
    })
    title.textContent = `👥 Your people (${rows.length})`
    body.replaceChildren()
    const hdr = el('div', 'roster-row roster-hdr')
    for (const h of ['Name', 'Age', 'Doing', 'Holds', 'Food', 'Health', 'Best at', '']) hdr.appendChild(el('span', 'r-c', h))
    body.appendChild(hdr)
    for (const u of rows) {
      const p = u.person!
      const row = el('div', 'roster-row' + (u.state === 'idle' && !u.scav ? ' r-idle' : ''))
      row.appendChild(el('span', 'r-c r-name', p.name))
      row.appendChild(el('span', 'r-c', String(ageYears(p))))
      row.appendChild(el('span', 'r-c r-act', unitActivity(st, u)))
      row.appendChild(p.held ? elHtml('span', 'r-c r-holds', itemIconSvg(p.held, 22)) : el('span', 'r-c', '·'))
      const rStarve = starvingHours(st, p)
      if (rStarve !== null) {
        row.appendChild(el('span', 'r-c starving', `⚠ ${rStarve.toFixed(1)}h`))
      } else {
        const food = el('span', 'r-c r-bar')
        food.appendChild(bar(Math.max(0, Math.min(1, (p.food ?? 100) / 100))))
        row.appendChild(food)
      }
      const hp = el('span', 'r-c r-bar')
      const maxHp = 20 + (p.attr?.vit ?? 0) * 4
      hp.appendChild(bar(Math.max(0, Math.min(1, u.hp / Math.max(1, maxHp)))))
      row.appendChild(hp)
      let bestK: SkillKey = 'build'
      let bestV = -1
      for (const k of SKILLS) {
        const v = p.skill?.[k] ?? 0
        if (v > bestV) { bestV = v; bestK = k }
      }
      const title = personTitle(p)
      row.appendChild(el('span', 'r-c', title || (bestV > 0.05 ? `${SKILL_LABEL[bestK]} ${bestV.toFixed(1)}` : '—')))
      const jump = el('button', 'btn btn-sm', '⌖')
      jump.addEventListener('pointerdown', (e) => {
        e.preventDefault()
        e.stopPropagation()
        jumpTo(u.q, u.r)
      })
      const jc = el('span', 'r-c')
      jc.appendChild(jump)
      row.appendChild(jc)
      row.addEventListener('pointerdown', (e) => {
        if (e.target === jump) return
        st.selection = { units: [u.id], building: null }
      })
      body.appendChild(row)
    }
    if (!rows.length) body.appendChild(el('div', 'roster-empty', 'No serfs yet — keep your folk fed and they will come.'))
  }

  return { toggle, isOpen, refresh }
}

// ---- Defeat overlay ---------------------------------------------------------------------

export function initDefeatOverlay(ctx: UICtx): { refresh: () => void } {
  const { st, tx, root } = ctx
  const back = el('div', 'modal-back defeat hidden')
  const panel = el('div', 'defeat-panel')
  back.appendChild(panel)
  root.appendChild(back)

  panel.appendChild(el('div', 'defeat-sigil', '🕯'))
  panel.appendChild(el('h1', 'defeat-title', 'Your village has fallen'))
  panel.appendChild(
    el(
      'p',
      'defeat-sub',
      'The hall lies in ash and the fields are silent. Yet survivors gather in the dark, half your stores upon their backs, waiting for a leader to raise a new banner.',
    ),
  )
  const btn = el('button', 'btn btn-gold defeat-btn', '⚜ Found a new village')
  panel.appendChild(btn)
  btn.onclick = () => {
    btn.disabled = true
    btn.textContent = 'Scouting new lands…'
    tx({ t: 'respawn' })
    setTimeout(() => {
      btn.disabled = false
      btn.textContent = '⚜ Found a new village'
    }, 4000)
  }

  function refresh(): void {
    const ruined = !!st.self?.ruined
    back.classList.toggle('hidden', !ruined)
  }

  return { refresh }
}

// ---- Chronicle (collapsible event log) ------------------------------------------------------

export function initChronicle(ctx: UICtx): { append: (ev: GameEvent) => void; toggle: () => void } {
  const { root } = ctx
  const wrap = el('div', 'chronicle collapsed')
  const head = el('button', 'chron-head')
  head.appendChild(el('span', 'chron-title', '📜 Chronicle'))
  // Unread badge: counts entries that arrived while the scroll was collapsed.
  const badge = el('span', 'chron-badge hidden', '0')
  head.appendChild(badge)
  let unread = 0
  const caret = el('span', 'chron-caret', '▸')
  head.appendChild(caret)
  const body = el('div', 'chron-body')
  wrap.appendChild(head)
  wrap.appendChild(body)
  root.appendChild(wrap)

  function toggle(): void {
    wrap.classList.toggle('collapsed')
    caret.textContent = wrap.classList.contains('collapsed') ? '▸' : '▾'
    if (!wrap.classList.contains('collapsed')) {
      body.scrollTop = 0
      unread = 0
      badge.classList.add('hidden')
    }
  }
  head.onclick = toggle

  function append(ev: GameEvent): void {
    if (wrap.classList.contains('collapsed')) {
      unread = Math.min(99, unread + 1)
      badge.textContent = String(unread)
      badge.classList.remove('hidden')
    }
    const row = el('div', 'chron-row k-' + ev.kind)
    row.appendChild(el('span', 'chron-dot'))
    row.appendChild(el('span', 'chron-msg', ev.msg))
    if (ev.q !== undefined && ev.r !== undefined) {
      const q = ev.q
      const r = ev.r
      const loc = el('button', 'chron-loc', '⌖')
      loc.title = 'Jump to ' + q + ',' + r
      loc.onclick = (e) => {
        e.stopPropagation()
        ctx.jumpTo(q, r)
      }
      row.appendChild(loc)
    }
    body.prepend(row)
    while (body.children.length > 120) body.lastElementChild!.remove()
  }

  return { append, toggle }
}
